// Florence, "Blue hour over the Arno". A late-May evening at the blue hour, seen from the top of the wide steps at
// the north-west corner of Piazzale Michelangelo (43.7629 N, 11.2650 E, about 54 m above the river), facing
// north-west (about 320 degrees): the view everyone pictures. Left to right, as they really stand from here: Ponte
// Vecchio with the further bridges behind it, the long flat Ponte alle Grazie in front, the Uffizi's river front,
// Palazzo Vecchio with Arnolfo's tower, the dome of San Lorenzo far off, the Badia's spire and the Bargello's tower,
// Giotto's campanile and Brunelleschi's dome, Santa Croce with the National Library before it, the green dome of the
// synagogue, and the hills rising towards Fiesole. The Arno comes in from the right at the foot of the hill, drops
// over the San Niccolo weir beside the old gate tower and runs away west under the bridges; with no rain it does the
// reflecting. Below us the steps go down to a small terrace with a stone balustrade and cast-iron lamps: people sit
// on the steps to watch the evening, a busker plays under a lamp, a three-wheeler sells coffee under a string of
// bulbs. Swifts wheel over the roofs, bats hunt round the lamps, fireflies blink in the trees of the slope, and rose
// petals from the Giardino delle Rose (just below, in bloom in May) drift by on a light breeze at three depths.
// The city's billboard is the hoarding on a restoration scaffold round a palazzo below the cathedral.
// render(t, { crowd }) thins the people; crowd 0 draws nobody at all (see CROWD).
// Truthful, but compressed: the real view spans about 65 degrees (Ponte Vecchio at 300, Palazzo Vecchio 315, the
// campanile 326, the dome 331, Santa Croce 336 to 344, the synagogue 6), so the monuments are drawn at telephoto
// size and pulled together without changing their order; the roofs between the cathedral and the river, and the
// river itself, are squeezed in height. Not in this view: the bronze David (behind us), Palazzo Pitti and Santo
// Spirito (left of the frame, behind the hill), the Uffizi's courtyard (only its river front shows), the Baptistery
// (behind the nave), San Miniato (behind and above us); Fiesole itself lies just right of the frame.
//
// The loop's timeline (seconds; see the constants under "Timed events"):
//   every 4-12 s   windows switch, the busker strums and nods, bats and swifts, the cat's tail, fireflies
//   every 30 s     the pair on the steps pass their bottle; the vendor pulls a coffee (a puff of steam)
//    4 ..  64      someone strolls across the terrace (and, the other way with a small dog, at 150 .. 214)
//   14 ..  66      a rowing scull comes up from Ponte alle Grazie, turns below the weir and rows back
//   30 ..  56      the little bus runs east along the lungarno; 48 Santa Croce chimes
//   58 .. 112      a jet draws a rose contrail across the sky
//   78 .. 132      a renaioli punt with a lantern drifts down to the bridge             (a gentle bigger moment)
//  100 .. 112      the campanile strikes five; pigeons lift off the dome and settle by 150  (bigger moment)
//  124 .. 140      the cat walks along the balustrade, and back at 206 .. 222
//  146 .. 198      the scull's second outing, its coach following in a launch            (bigger moment)
//  166 .. 192      the bus comes back west
//  196             the Badia chimes; 198 .. 206 the pair on the steps raise their bottle to the busker
//  222 .. 236      a party of swifts streaks low over the roofs (and at 20 .. 34)
//  66, 136         the hoarding's right-hand lamp sputters; 89, 180 one lantern of the left lamp
//  every 20 s      a car's lamps cross Ponte alle Grazie
import { W, H, LOOP, TAU, frac, clamp, lerp, smooth, wave, step, cycle, scroll, hash, rng, bayer, hex,
  makeCanvas, makeHalo, drawHalo, text, streak, makePitch } from '../engine/core.js';
import { weatherPlans, drawWeather } from '../engine/weather.js';

let ctx;                                              // the scene's 480x270 context, set by create()
const halo = (hl, x, y, a = 1) => drawHalo(ctx, hl, x, y, a);

/* ================= Layout ================= */
const DUOMO = { cx: 270, tip: 24, top: 48, spring: 84, drum: 97, R: 27.5 };   // Brunelleschi's dome (1 px = about 1 m)
const CAMP = { x0: 180, x1: 192, top: 54 };           // Giotto's campanile
const PV = { x0: 100, x1: 142, top: 98, tx: 108, tw: 8, tip: 37 };            // Palazzo Vecchio's block and Arnolfo's tower
const PONTE = { x0: 14, x1: 80 };                     // Ponte Vecchio
const GRAZIE = { x0: 60, y0: 163, x1: 150, y1: 158 }; // Ponte alle Grazie: south end, north end (waterline)
const TOWER = { x0: 310, x1: 326, top: 158 };         // Torre San Niccolo, on the near bank below us
const WEIR = { x0: 338, x1: 438 };                    // the pescaia: from the near bank up to the far one
const RAIL = 198, FLOOR = 208, FOOT = 216;            // the lower terrace: balustrade rail, paving, foot of the steps
const STEPS = [224, 234, 246, 260];                   // far edge of each step up towards us (people sit on them)
const LAMPS = [186, 382];                             // the terrace's cast-iron lamps (x of the post)
const LAMP_Y = 160;                                   // row of their lanterns
const CART = { x: 88, y: 190 };                       // the three-wheeler's box (top-left)
const BILL = { x: 214, y: 128, w: 48, h: 20 };        // the hoarding's face on the scaffold (the site's pitch is painted here)
const BILL_LAMPS = [BILL.x + 8, BILL.x + 24, BILL.x + 40], BILL_LAMP_Y = BILL.y - 5;
const MOON = { x: 150, y: 21 };
const GLOW = { x: 96, y: 92 };                       // where the sun went down, behind the hills in the north-west
/** the far bank's waterline (and, left of x = 80, the foot of Ponte Vecchio): the river comes nearer to the right */
const bankY = x => (x < 80 ? Math.round(156.6 - (x - 14) * 3 / 66) : Math.round(150 + 34 * Math.pow(x / 480, 1.3)));
/** the near bank's line (left part of the frame; right of x = 240 the terrace hides it) */
const SB = [[0, 156], [14, 156], [60, 163], [120, 176], [200, 192], [240, 200], [480, 200]];
function southY(x) {
  for (let i = 1; i < SB.length; i++) if (x <= SB[i][0]) { const a = SB[i - 1], b = SB[i]; return Math.round(lerp(a[1], b[1], (x - a[0]) / (b[0] - a[0]))); }
  return 200;
}
const WDEPTH = 46;                                    // rows of water kept below the far bank

// The share of the people the scene shows by default: 1 = everyone, about half here, for a calm terrace.
// render(t, { crowd }) overrides it for one frame. Everyone has a fixed rank in [0, 1) and is drawn while
// rank < crowd; rank 0 (the busker, the coffee vendor) stays while anybody does. Crowd 0 is nobody at all: the cast
// goes too, with what is theirs (the guitar and its case, the easel, the bottle, the tripod, the rowed scull and the
// punt); the cart stands lit and unattended, the scooter stays parked, the coach's launch and the bus still pass.
const CROWD = 0.5;
const here = (rank, crowd) => crowd > 0 && (rank === 0 || rank < crowd);

/* ================= Timed events (loop seconds) ================= */
const SCULL = [{ t0: 14, t1: 66, coach: false }, { t0: 146, t1: 198, coach: true }];   // out from the bridge, a turn, and back
const BUS = [{ t0: 30, t1: 56, dir: 1 }, { t0: 166, t1: 192, dir: -1 }];
const JET = { t0: 58, t1: 112 };
const PUNT = { t0: 78, t1: 132 };
const BELLS = [100, 103, 106, 109, 112];              // the campanile strikes
const FLOCK_T0 = 100, FLOCK_T1 = 150;                 // the dome's pigeons start up on the first stroke, and settle again
const CAT_WALK = [[124, 140, 1], [206, 222, -1]];     // along the balustrade and back
const CHIME_T = 196, CROCE_CHIME_T = 48;              // the Badia's bell, and Santa Croce's
const TOAST = [198, 206];                             // the pair on the steps raise their bottle
const PARTY = [[20, 34], [222, 236]];                 // a screaming party of swifts, low over the roofs
const SERVE_EVERY = 30;                               // the vendor pulls a coffee
const BILL_FLICK = [[66.2, 67.8], [136.4, 137.6]];    // the hoarding's right-hand lamp sputters
const LAMP_FLICK = [[88.6, 89.9], [180.3, 181.4]];    // and one lantern of the left lamp

/* ================= Pixel helpers ================= */
const RGB = {};
const C = s => RGB[s] || (RGB[s] = hex(s));
const toHex = (r, g, b) => '#' + ((1 << 24) | (r << 16) | (g << 8) | b).toString(16).slice(1);
const MIXED = {};
function mixHex(a, b, t) {
  const k = a + b + t;
  if (!MIXED[k]) { const A = C(a), B = C(b); MIXED[k] = toHex(...A.map((v, i) => Math.round(lerp(v, B[i], t)))); }
  return MIXED[k];
}
/** ImageData-backed painter for prerendering */
function pix(w, h) {
  const [c, x] = makeCanvas(w, h), img = x.createImageData(w, h), d = img.data;
  return {
    c, x, w, h, d,
    set(i, j, col, a = 255) {
      i = Math.round(i); j = Math.round(j);
      if (i < 0 || j < 0 || i >= w || j >= h) return;
      const o = (j * w + i) * 4, v = typeof col === 'string' ? C(col) : col;
      d[o] = v[0]; d[o + 1] = v[1]; d[o + 2] = v[2]; d[o + 3] = a;
    },
    alpha(i, j) { return (i < 0 || j < 0 || i >= w || j >= h) ? 0 : d[(j * w + i) * 4 + 3]; },
    clear(i, j) { if (i >= 0 && j >= 0 && i < w && j < h) d[(j * w + i) * 4 + 3] = 0; },
    fill(i, j, ww, hh, col) { for (let y = j; y < j + hh; y++) for (let k = i; k < i + ww; k++) this.set(k, y, col); },
    line(x0, y0, x1, y1, col) {
      const n = Math.max(Math.abs(x1 - x0), Math.abs(y1 - y0), 1);
      for (let k = 0; k <= n; k++) this.set(lerp(x0, x1, k / n), lerp(y0, y1, k / n), col);
    },
    text(s, i, j, col) { x.putImageData(img, 0, 0); text(x, s, i, j, col); d.set(x.getImageData(0, 0, w, h).data); },
    done() { x.putImageData(img, 0, 0); return c; },
  };
}
/** ordered-dithered pick from a colour ramp, v in 0..1 (soft < 1 narrows the dithered band) */
function pick(ramp, v, i, j, soft = 1) {
  const f = clamp(v, 0, 1) * (ramp.length - 1);
  let k = Math.floor(f);
  if (f - k > 0.5 + (bayer(i, j) - 0.5) * soft) k++;
  return ramp[Math.min(k, ramp.length - 1)];
}
/** smooth value noise in 0..1 (build time only); px: period in x for tileable layers */
function vnoise(x, y, s, px = 0) {
  const xi = Math.floor(x), yi = Math.floor(y), fx = x - xi, fy = y - yi;
  const X0 = px ? ((xi % px) + px) % px : xi, X1 = px ? (((xi + 1) % px) + px) % px : xi + 1;
  const a = hash(X0, yi, s), b = hash(X1, yi, s), c = hash(X0, yi + 1, s), d = hash(X1, yi + 1, s);
  const u = fx * fx * (3 - 2 * fx), v = fy * fy * (3 - 2 * fy);
  return lerp(lerp(a, b, u), lerp(c, d, u), v);
}
/** crop a painter to its opaque bounding box (build time); draw the result with blit() */
function trim(p) {
  let x0 = p.w, y0 = p.h, x1 = -1, y1 = -1;
  for (let y = 0; y < p.h; y++) for (let i = 0; i < p.w; i++) if (p.d[(y * p.w + i) * 4 + 3]) {
    if (i < x0) x0 = i; if (i > x1) x1 = i; if (y < y0) y0 = y; if (y > y1) y1 = y;
  }
  const full = p.done();
  if (x1 < 0) return { c: makeCanvas(1, 1)[0], x: 0, y: 0 };
  const [c, x] = makeCanvas(x1 - x0 + 1, y1 - y0 + 1);
  x.drawImage(full, -x0, -y0);
  return { c, x: x0, y: y0 };
}
/** sprite from rows of characters and a colour key ('.' or unknown = transparent) */
function sprite(rows, key, flip = false) {
  const h = rows.length, w = Math.max(...rows.map(r => r.length)), p = pix(w, h);
  rows.forEach((row, y) => { for (let i = 0; i < row.length; i++) { const col = key[row[i]]; if (col) p.set(flip ? w - 1 - i : i, y, col); } });
  return p.done();
}
const blit = (o, a = 1) => { ctx.globalAlpha = a; ctx.drawImage(o.c, o.x, o.y); ctx.globalAlpha = 1; };
const R = (x, y, w, h, c) => { ctx.fillStyle = c; ctx.fillRect(x, y, w, h); };
const dot = (x, y, c) => { ctx.fillStyle = c; ctx.fillRect(Math.round(x), Math.round(y), 1, 1); };
const inWin = (t, [a, b]) => t >= a && t < b;
function flick(t, list, id) {                         // a lamp that sputters inside the given windows
  for (const [a, b] of list) if (t >= a && t < b) { const hv = hash(id, Math.floor(t * 20)); return hv > 0.45 ? 1 : hv > 0.2 ? 0.45 : 0.12; }
  return 1;
}

/* ================= Sky: the blue hour, the last warm band low over the hills in the north-west ================= */
const SKY = ['#0e1640', '#111c4e', '#15245c', '#1a2d6a', '#213878', '#2a4484', '#36518e', '#465f96', '#5a6c9c',
  '#72779e', '#8c809e', '#a8889c', '#c29294', '#da9e8a', '#ecac7e', '#f8be7c', '#fdd08a'];
const SKY_H = 176;
function skyV(i, y) {
  let v = Math.pow(Math.min(1, y / 108), 1.25) * 0.54;                                      // deep blue down to a pale horizon
  v += Math.max(0, 1 - Math.hypot((i - GLOW.x) / 300, (y - GLOW.y) / 64)) ** 1.4 * 0.56;    // the afterglow
  v += Math.max(0, 1 - Math.hypot((i - GLOW.x) / 520, (y - 100) / 26)) * 0.14;             // and its band along the horizon
  return v;
}
function renderSky() {
  const p = pix(W, SKY_H);
  for (let y = 0; y < SKY_H; y++) for (let i = 0; i < W; i++) p.set(i, y, pick(SKY, skyV(i, y), i, y));
  return p.done();
}
const STARS = [];
{
  const r = rng(12);
  while (STARS.length < 46) {
    const x = Math.floor(r() * W), y = 2 + Math.floor(r() * 74);
    if (skyV(x, y) > 0.26 || Math.hypot(x - MOON.x, y - MOON.y) < 14) continue;            // none in the glow or on the moon
    STARS.push({ x, y, k: 24 + Math.floor(r() * 60), ph: r(), b: 0.45 + r() * 0.55, big: r() < 0.14 });
  }
}
/** a young crescent, horns up and away from the sunset, the dark limb faintly earthlit */
function renderMoon() {
  const r = 7, s = 15, p = pix(s, s);
  for (let y = 0; y < s; y++) for (let i = 0; i < s; i++) {
    const dx = i - 7, dy = y - 7, dd = Math.hypot(dx, dy);
    if (dd > r + 0.3) continue;
    const sh = Math.hypot(dx - 3.3, dy + 2.8);                         // the shadow disc, towards the upper right
    if (sh < r - 0.1) { p.set(i, y, dd > r - 0.9 ? '#2c3c7a' : '#22306c'); continue; }
    p.set(i, y, sh < r + 1 ? '#f6e4c0' : '#fff6de');
  }
  return p.done();
}

/* ================= Thin clouds (tileable, drifting; their bellies still catch the afterglow) ================= */
const CLOUD_DEFS = [
  { seed: 17, y: -10, h: 54, banks: 5, k: 1, rx: [22, 48], ry: [2, 4], cut: 0.5,
    pal: { top: '#3a4684', body: '#2c3674', core: '#28306a', bot: '#484c86', bot2: '#363e7a' } },
  { seed: 31, y: 22, h: 56, banks: 5, k: 2, rx: [20, 44], ry: [2, 4], cut: 0.45,
    pal: { top: '#56609a', body: '#444e8c', core: '#3e4684', bot: '#9272a0', bot2: '#665c96' } },
  { seed: 47, y: 62, h: 44, banks: 5, k: 1, rx: [22, 50], ry: [1.6, 3], cut: 0.4,
    pal: { top: '#74709e', body: '#626296', core: '#5a5c90', bot: '#e89a7a', bot2: '#b07c92' } },
];
function makeCloudLayer(def) {
  const r = rng(def.seed), h = def.h, puffs = [];
  for (let b = 0; b < def.banks; b++) {
    const bx = (b + r() * 0.8) * W / def.banks, bw = 70 + r() * 110, base = h * (0.14 + ((b * 2 + 1) % def.banks + 0.2 + r() * 0.6) / def.banks * 0.74);   // spread evenly in height
    const np = 5 + Math.floor(r() * 5);
    for (let i = 0; i < np; i++) {
      const rx = lerp(def.rx[0], def.rx[1], r()), ry = lerp(def.ry[0], def.ry[1], r());
      puffs.push({ x: bx + (r() - 0.5) * bw, y: base - r() * ry * 1.4, rx, ry, base: base + ry * 0.3 });
    }
  }
  const D = new Float32Array(W * h);
  for (let y = 0; y < h; y++) for (let x = 0; x < W; x++) {
    let best = -9;
    for (const q of puffs) {
      let dx = x - q.x; dx -= Math.round(dx / W) * W;
      const dy = y - q.y;
      let v = 1 - (dx * dx) / (q.rx * q.rx) - (dy * dy) / (q.ry * q.ry);
      if (y > q.base) v -= (y - q.base) * def.cut;
      if (v > best) best = v;
    }
    best += 0.2 * Math.sin(TAU * 13 * x / W + y * 0.6 + def.seed) * Math.sin(TAU * 7 * x / W - y * 0.33);
    D[y * W + x] = best;
  }
  const get = (x, y) => (y < 0 || y >= h) ? -9 : D[y * W + ((x % W) + W) % W];
  const p = pix(W, h), P = def.pal;
  for (let y = 0; y < h; y++) for (let x = 0; x < W; x++) {
    const th = (bayer(x, y) - 0.5) * 0.36, v = D[y * W + x];
    if (v + th <= 0.03) continue;
    let col;
    if (get(x, y - 1) + th <= 0.03) col = P.top;
    else if (get(x, y + 1) + th <= 0.03) col = P.bot;
    else if (get(x, y + 2) <= 0.05 + bayer(x, y) * 0.2) col = P.bot2;
    else if (v > 0.55 + th) col = P.core;
    else col = P.body;
    p.set(x, y, col);
  }
  return { c: p.done(), y: def.y, k: def.k };
}

/* ================= The hills: Monte Morello far off, the slopes rising to Fiesole on the right ================= */
const hillFar = x => 90 - 10 * Math.exp(-(((x - 190) / 110) ** 2)) - 3 * Math.exp(-(((x - 40) / 60) ** 2)) + 1.4 * Math.sin(x * 0.045) - Math.max(0, x - 340) * 0.02;
const hillNear = x => 102 + 2 * Math.sin(x * 0.031 + 1) + 1.1 * Math.sin(x * 0.11) - smooth(300, 480, x) * 15 + 3 * Math.exp(-(((x - 432) / 9) ** 2));
const HILL_LIGHTS = [];
function renderHills() {
  const p = pix(W, 130), r = rng(53);
  const FAR = ['#343b68', '#3d4474', '#4d4c7c', '#655380', '#855c80'], NEAR = ['#1d2348', '#242a52', '#2c3058', '#3a345c', '#4c3a5e'];
  for (let i = 0; i < W; i++) {
    const g = clamp(1 - Math.abs(i - GLOW.x) / 260, 0, 1);            // warmer and paler towards the afterglow
    const yf = Math.round(hillFar(i)), yn = Math.round(hillNear(i));
    for (let y = yf; y < 130; y++) p.set(i, y, pick(FAR, g * 0.8 + (y === yf ? 0.2 : 0) + (y - yf) * 0.012 + (vnoise(i / 9, y / 4, 3) - 0.5) * 0.3, i, y));
    for (let y = yn; y < 130; y++) p.set(i, y, pick(NEAR, g * 0.7 + (y === yn ? 0.25 : 0) + (vnoise(i / 6, y / 3, 5) - 0.5) * 0.4, i, y));
  }
  // cypress rows and umbrella pines on the near ridges, villas with a lamp or two
  for (let i = 4; i < W; i += 3 + Math.floor(r() * 9)) {
    const yn = Math.round(hillNear(i)), kind = r();
    if (kind < 0.55) { const hgt = 2 + Math.floor(r() * 3); for (let k = 0; k < hgt; k++) p.set(i, yn - 1 - k, '#141a38'); }
    else if (kind < 0.7) { p.fill(i - 1, yn - 3, 4, 2, '#161c3c'); p.set(i, yn - 1, '#141a38'); }
    else if (kind < 0.85) {
      const vy = yn + 2 + Math.floor(r() * 8);
      p.fill(i, vy, 4, 2, '#6c6078'); p.fill(i, vy - 1, 4, 1, '#5a3c4c');
      HILL_LIGHTS.push({ x: i + 1 + Math.floor(r() * 2), y: vy + 1, k: 30 + Math.floor(r() * 60), ph: r() });
    }
  }
  // Fiesole on its saddle at the right edge: the cathedral's thin tower and a cluster of lights
  p.fill(431, Math.round(hillNear(432)) - 6, 2, 7, '#4a4266'); p.fill(431, Math.round(hillNear(432)) - 7, 1, 1, '#5a5070');
  for (let k = 0; k < 16; k++) { const x = 414 + Math.floor(r() * 40); HILL_LIGHTS.push({ x, y: Math.round(hillNear(x)) + 1 + Math.floor(r() * 7), k: 30 + Math.floor(r() * 70), ph: r() }); }
  for (let k = 0; k < 26; k++) { const x = Math.floor(r() * W); HILL_LIGHTS.push({ x, y: Math.round(hillNear(x)) + 2 + Math.floor(r() * 9), k: 30 + Math.floor(r() * 70), ph: r() }); }
  return trim(p);
}

/* ================= Evening haze over the valley (tileable, drifting, breathing a little) ================= */
const HAZE_DEFS = [
  { seed: 5, y: 84, h: 40, k: 1, dir: 1, top: '#7c76a0', bot: '#b08c98', alpha: 0.46 },
  { seed: 9, y: 118, h: 44, k: 1, dir: -1, top: '#6a6c98', bot: '#8a7c98', alpha: 0.3 },
];
function makeHaze(def) {
  const h = def.h, p = pix(W, h), A = C(def.top), B = C(def.bot);
  for (let y = 0; y < h; y++) for (let x = 0; x < W; x++) {
    const v = y / (h - 1), prof = Math.pow(Math.sin(Math.PI * Math.pow(v, 0.8)), 1.4);
    const nz = 0.5 + 0.3 * Math.sin(TAU * 2 * x / W + def.seed + y * 0.06) + 0.18 * Math.sin(TAU * 5 * x / W - y * 0.21 + def.seed * 2)
      + 0.12 * Math.sin(TAU * 11 * x / W + y * 0.43) + 0.08 * Math.sin(TAU * 23 * x / W - y * 0.7);
    const q = Math.floor(Math.min(1, prof * clamp(nz, 0, 1) * 1.1) * 4 + bayer(x, y)) / 4;
    if (q <= 0) continue;
    p.set(x, y, [lerp(A[0], B[0], v), lerp(A[1], B[1], v), lerp(A[2], B[2], v)], Math.round(Math.min(1, q) * 255));
  }
  return { ...def, c: p.done() };
}

/* ================= The city's materials ================= */
const TERRA = ['#3e2638', '#55303a', '#6c3c3c', '#844a3e', '#9c5a42', '#b46c48'];              // roof tiles at dusk
const WALL = ['#3a3048', '#54424c', '#705850', '#8e7258', '#ac8c62', '#c8a672'];               // plaster: shadow to lamplit ochre
const STONE = ['#3a2a3a', '#584040', '#7a5644', '#9e724a', '#c29254', '#e2b668', '#f8d88c'];   // floodlit pietraforte
const CROCE = ['#3a2c38', '#5a4644', '#7c6250', '#a08268', '#c2a684', '#dcc6a2', '#eedcbc'];   // Santa Croce's campanile, lit softer
const MARBLE = ['#463e58', '#6a5e72', '#928488', '#b8a89a', '#d8caae', '#f0e4c6', '#fff6e0'];  // the cathedral's white marble
const TILE = ['#5a2834', '#763234', '#923e30', '#ae4c36', '#c65e3e', '#dc7448', '#ee9058'];    // the dome's terracotta
const GREEN_M = '#56786a', PINK_M = '#cc8c80', GLASS = '#2a2240', LIT = ['#ffd98c', '#ffc46a', '#ffe8b4', '#f2b25c'];
const WINS = [];                                      // windows that switch: { x, y, w, h, n, p, off, col, under }
const TWINK = [];                                     // street lamps seen far off, twinkling: { x, y, k, ph }
let WIN_LAYER = 0;                                     // which layer addWin's windows belong to: 0 back, 1 front, 2 the palazzo, 3 the near bank
const addWin = (r, x, y, w, h, under = false) => WINS.push({ id: WINS.length, x, y, w, h, n: 4 + Math.floor(r() * 7), p: 0.35 + r() * 0.3,
  off: r(), col: LIT[Math.floor(r() * LIT.length)], under, layer: WIN_LAYER });
const isCol = (p, x, y, c) => { const o = (y * p.w + x) * 4; return p.d[o] === c[0] && p.d[o + 1] === c[1] && p.d[o + 2] === c[2] && p.d[o + 3] === 255; };
const isGlass = (p, w) => { const G = C(GLASS); for (let j = 0; j < w.h; j++) for (let i = 0; i < w.w; i++) if (!isCol(p, w.x + i, w.y + j, G)) return false; return true; };

/* ================= Brunelleschi's dome, the lantern, the tribunes and the nave ================= */
const domeHW = y => { const g = DUOMO.spring - y; return Math.sqrt(1600 - g * g) - 12.5; };   // the pointed-fifth profile
const RIBS = [-0.78, -0.11, 0.62];                    // the white ribs between the faces we see: SW | S | SE | E
const faceOf = f => (f < RIBS[0] ? 0 : f < RIBS[1] ? 1 : f < RIBS[2] ? 2 : 3);
const FACE_V = [0.5, 0.74, 0.54, 0.26];               // the south face still catches the west; the east one is in shadow
const PALLA_Y = 27;                                   // top row of the lantern's gilt ball (the cross stands on it)
const PALLA = [['#9a7434', '#ffe9a0', '#a07a38'], ['#ffeaa8', '#fff8dc', '#c89840'], ['#b8863a', '#e8b048', '#7e5a2c']];
function drawDuomo(p) {
  const cx = DUOMO.cx;
  // --- the nave, running west (left) to the facade behind the campanile
  for (let i = 192; i <= cx - 20; i++) for (let y = 93; y <= 119; y++) {
    const m = (i - 196) % 11;
    let col;
    if (y <= 96) col = pick(TILE, 0.46 - (y - 93) * 0.07 + (y === 93 ? 0.22 : 0), i, y, 0.6);            // nave roof
    else if (y <= 105) {                                                                               // clerestory, its round windows
      col = pick(MARBLE, 0.6 + (y - 97) * 0.02 - (m === 0 ? 0.16 : 0), i, y, 0.5);
      if (y === 97) col = '#fff2d6'; else if (y === 105) col = GREEN_M;
      else if ((m === 5 || m === 6) && (y === 100 || y === 101)) col = GLASS;
    } else if (y <= 109) col = pick(TILE, 0.34 - (y - 106) * 0.06 + (y === 106 ? 0.2 : 0), i, y, 0.6);  // aisle roof
    else {
      col = pick(MARBLE, 0.78 + (y - 110) * 0.015 - (m === 0 ? 0.14 : 0), i, y, 0.5);                    // aisle wall, floodlit
      if (y === 110) col = PINK_M; else if ((m === 5 || m === 6) && y >= 112 && y <= 117) col = y === 112 && m === 6 ? '#b8a89a' : GLASS;
    }
    p.set(i, y, col);
  }
  // --- the tribunes: half-domes over the south and east apses, a little exedra between them
  for (const [tc, sh] of [[cx - 19, 0], [cx + 19, -0.22]]) {
    for (let y = 99; y <= 107; y++) {
      const hw = 13 * Math.sqrt(1 - ((107 - y) / 9.5) ** 2);
      for (let i = Math.ceil(-hw); i <= Math.floor(hw); i++) {
        const f = i / hw;
        let col = pick(TILE, 0.56 + sh - f * 0.2 + (y - 99) * 0.02, tc + i, y, 0.7);
        if (Math.abs(Math.abs(i) - Math.round(hw * 0.55)) < 0.5 || i === 0) col = sh ? '#b4a49c' : '#f2e2c6';   // little ribs
        if (i === Math.ceil(-hw)) col = '#f2e2c6';
        p.set(tc + i, y, col);
      }
    }
    for (let y = 108; y <= 120; y++) for (let i = -13; i <= 13; i++) {
      let col = pick(MARBLE, 0.74 + sh + (y - 108) * 0.015 - i * 0.008, tc + i, y, 0.5);
      if (y === 108) col = sh ? '#cfc0aa' : '#fff2d6'; else if (y === 109) col = GREEN_M;
      else if ((i + 13) % 6 === 3 && y >= 111 && y <= 117) col = GLASS;
      else if ((i + 13) % 6 === 0) col = sh ? '#928488' : '#d8caae';
      p.set(tc + i, y, col);
    }
  }
  for (let y = 103; y <= 120; y++) for (let i = -4; i <= 4; i++) {                                       // the exedra
    let col = pick(MARBLE, 0.7 - i * 0.02, cx + i, y, 0.5);
    if (y <= 105) { if (Math.abs(i) > (y - 102) + 1) continue; col = pick(TILE, 0.5 - i * 0.04, cx + i, y, 0.6); }
    else if (Math.abs(i) <= 1 && y >= 109 && y <= 115 && !(y === 109 && i)) col = GLASS;
    p.set(cx + i, y, col);
  }
  // --- the drum: marble panels and one round window a face; the "cricket cage" gallery on the south-east face only
  for (let y = DUOMO.spring + 1; y <= DUOMO.drum; y++) for (let i = -28; i <= 28; i++) {
    const f = i / 28, fc = faceOf(f);
    let col = pick(MARBLE, FACE_V[fc] + 0.16 + (y - 85) * 0.012, cx + i, y, 0.5);
    if (RIBS.some(q => Math.abs(i - Math.round(q * 28)) < 0.5) || Math.abs(i) === 28) col = fc === 3 ? '#b8a89a' : '#fff6e0';
    else if (y === DUOMO.spring + 2 || y === DUOMO.drum - 1) col = fc === 3 ? '#4a6458' : GREEN_M;
    else if (y === DUOMO.drum) col = fc === 3 ? '#b8a89a' : '#f0e4c6';
    p.set(cx + i, y, col);
  }
  for (const [ox, rr] of [[-12, 2.7], [7, 2.7], [23, 1.6], [-25, 1.3]]) for (let dy = -3; dy <= 3; dy++) for (let dx = -3; dx <= 3; dx++) {
    const dd = Math.hypot(dx, dy);
    if (dd < rr) p.set(cx + ox + dx, 91 + dy, dd < rr - 1 ? GLASS : '#e8dcc0');
  }
  for (let i = -28; i <= 28; i++) {
    const fc = faceOf(i / 28);
    p.set(cx + i, DUOMO.spring + 1, fc === 2 ? ((i & 1) ? '#fff6e0' : '#6a5e72') : '#7a5a4c');
    if (fc === 2) p.set(cx + i, DUOMO.spring, '#fff6e0');
  }
  // --- the dome: four faces of tile between white ribs, lit from below and from the west
  for (let y = DUOMO.top; y <= DUOMO.spring; y++) {
    const hw = domeHW(y), L = Math.ceil(-hw), Rr = Math.floor(hw);
    for (let i = L; i <= Rr; i++) {
      const f = i / hw, fc = faceOf(f);
      let v = FACE_V[fc] + (y - DUOMO.top) / 36 * 0.2 - (f - (fc ? RIBS[fc - 1] : -1)) * 0.14 + (vnoise(i / 3, y / 2, 21) - 0.5) * 0.12;
      if ((y - DUOMO.top) % 6 === 5) v -= 0.07;                                                         // tile courses
      let col = pick(TILE, v, cx + i, y, 0.8);
      if (RIBS.some(q => Math.abs(i - Math.round(q * hw)) < 0.5)) col = fc === 3 ? '#cdb8aa' : '#fbeed6';
      if (i === L) col = '#ffe6bc'; else if (i === Rr) col = '#8a6672';
      if (y === DUOMO.spring && fc !== 2) col = '#e8d8bc';
      p.set(cx + i, y, col);
    }
  }
  // --- the lantern: platform, buttresses, eight tall windows, a cone, the gilt ball and the cross
  p.fill(cx - 5, 47, 11, 1, '#fff6e0'); p.fill(cx - 5, 46, 11, 1, '#d8caae');
  for (let y = 36; y <= 45; y++) for (let i = -3; i <= 3; i++) {
    let col = i < 2 ? '#f6ead0' : '#b8a89a';
    if ((i === -2 || i === 0 || i === 2) && y >= 37 && y <= 43) col = y === 37 ? '#6a5e72' : '#2a2240';
    p.set(cx + i, y, col);
  }
  for (const [dx, y0] of [[-5, 43], [-4, 41], [4, 41], [5, 43]]) p.fill(cx + dx, y0, 1, 46 - y0, dx < 0 ? '#f0e4c6' : '#a89a94');   // volutes
  p.fill(cx - 4, 35, 9, 1, '#fff6e0');
  for (let y = 30; y <= 34; y++) { const hw = Math.round((y - 30) * 0.7); for (let i = -hw; i <= hw; i++) p.set(cx + i, y, i < 0 ? '#e8dcc4' : i === 0 ? '#fff6e0' : '#a89a98'); }
  // Verrocchio's palla on the cone's tip, bronze-gilt and round: the west's last light on its upper left, the
  // floodlights warm under it, its corners dimmed into the sky; the cross above it
  PALLA.forEach((row, j) => row.forEach((col, i) => p.set(cx - 1 + i, PALLA_Y + j, col)));
  p.fill(cx, PALLA_Y - 4, 1, 4, '#ffe9a0'); p.set(cx, PALLA_Y - 4, '#fff6d0'); p.set(cx - 1, PALLA_Y - 3, '#f0cc70'); p.set(cx + 1, PALLA_Y - 3, '#c89840');
}

/* ================= Giotto's campanile: white, green and pink marble, floodlit from the piazza ================= */
const CAMP_E = CAMP.x0 + 9;                           // the south face is x0..x0+8, the east face the 4 px right of it
const BELFRY = [[CAMP.x0 + 3, 62, 1, 14], [CAMP.x0 + 5, 62, 1, 14], [CAMP_E + 1, 63, 2, 13]];   // its openings (they glow on a stroke)
function drawCampanile(p) {
  const { x0, x1, top } = CAMP;
  for (let y = top; y <= 122; y++) for (let i = x0; i <= x1; i++) {
    const east = i >= CAMP_E;
    let v = (east ? 0.5 : 0.8) + (y - top) / 70 * 0.14 + (vnoise(i / 2, y / 3, 9) - 0.5) * 0.08;
    if (i === x0) v += 0.14; else if (i === x1) v -= 0.16; else if (i === CAMP_E) v -= 0.12; else if (i === CAMP_E - 1) v += 0.06;
    let col = pick(MARBLE, v, i, y, 0.6);
    if ([79, 92, 105].includes(y)) col = east ? '#46625a' : GREEN_M;
    else if ([80, 93, 106].includes(y)) col = east ? '#9c7672' : PINK_M;
    else if (y > 58 && y < 78 && (i === x0 + 1 || i === x0 + 7)) col = east ? '#928488' : '#c8b8a4';        // panel frames
    for (const [a, b] of [[83, 89], [96, 102]]) if (y >= a && y <= b && (i === x0 + 2 || i === x0 + 6 || i === CAMP_E + 2) && !(y === a && east)) col = GLASS;
    if (y > 108 && (i - x0) % 4 === 2 && y % 6 < 3 && !east) col = '#c8b8a4';                              // the lower panels' reliefs
    p.set(i, y, col);
  }
  for (const [bx, by, bw, bh] of BELFRY) p.fill(bx, by, bw, bh, GLASS);
  p.set(x0 + 4, 61, '#463e58'); p.set(x0 + 4, 76, '#f0e4c6');
  for (let i = x0 - 1; i <= x1 + 1; i++) {                                                                // the crown on its corbels
    p.set(i, top, i > CAMP_E ? '#d8caae' : '#fff6e0'); p.set(i, top + 1, i > CAMP_E ? '#b8a89a' : '#f0e4c6');
    p.set(i, top + 2, (i & 1) ? '#6a5e72' : '#d8caae');
    if ((i & 1) === 0) p.set(i, top - 1, i > CAMP_E ? '#b8a89a' : '#f0e4c6');
  }
}

/* ================= Palazzo Vecchio and Arnolfo's tower; the Badia, the Bargello, San Lorenzo's dome ================= */
function stoneV(i, y, x0, x1, top, base) {            // floodlit from below, the right side falling into shadow
  return 0.42 + (y - top) / (base - top) * 0.34 - (i - x0) / (x1 - x0) * 0.22 + (vnoise(i / 2, y / 2, 13) - 0.5) * 0.16;
}
function drawPalazzoVecchio(p, r) {
  const { x0, x1, top, tx, tw, tip } = PV;
  for (let y = top + 6; y <= 152; y++) for (let i = x0; i <= x1; i++) {                                   // the block
    let col = pick(STONE, stoneV(i, y, x0, x1, top, 150) + (i === x0 ? 0.14 : 0), i, y, 0.8);
    if ((y - top) % 12 === 9) col = pick(STONE, 0.2, i, y);                                               // string courses
    p.set(i, y, col);
  }
  for (const wy of [top + 11, top + 23]) for (let i = x0 + 3; i < x1 - 2; i += 5) {                       // round-arched two-light windows
    p.set(i, wy, GLASS); p.fill(i, wy + 1, 2, 3, GLASS); p.set(i + 1, wy, pick(STONE, 0.3, i, wy));
    if (r() < 0.3) p.fill(i, wy + 1, 2, 3, LIT[0]); else if (r() < 0.35) addWin(r, i, wy + 1, 2, 3);
  }
  for (let i = x0 - 1; i <= x1 + 1; i++) {                                                                // the gallery on its corbels
    for (let y = top; y <= top + 5; y++) {
      let col = pick(STONE, 0.66 - (i - x0) / 60 + (y - top) * 0.04, i, y, 0.6);
      if (y === top + 5) col = (i % 3 === 0) ? '#e2b668' : '#3a2a3a';                                     // lit corbel arches
      else if (y === top + 2 && i % 3 === 1) col = '#584040';
      else if (y === top && i % 3 === 2) continue;                                                        // merlons
      p.set(i, y, col);
    }
  }
  for (let y = tip + 26; y < top; y++) for (let i = tx; i < tx + tw; i++)                                 // the tower's shaft
    p.set(i, y, pick(STONE, 0.4 + (y - tip) / 70 * 0.36 - (i - tx) * 0.04 + (i === tx ? 0.14 : 0), i, y, 0.8));
  for (const wy of [72, 84]) p.fill(tx + 3, wy, 2, 3, GLASS);
  for (let i = tx - 2; i < tx + tw + 2; i++) for (let y = tip + 19; y <= tip + 25; y++) {                 // its own gallery
    let col = pick(STONE, 0.7 - (i - tx) * 0.03 + (y - tip - 19) * 0.04, i, y, 0.6);
    if (y === tip + 25) col = (i & 1) ? '#f8d88c' : '#3a2a3a'; else if (y === tip + 19 && (i - tx) % 3 === 0) continue;
    else if (y === tip + 20 && (i - tx) % 3 === 0) col = '#584040';
    p.set(i, y, col);
  }
  for (let y = tip + 8; y <= tip + 18; y++) for (let i = tx; i < tx + tw; i++) {                          // the belfry on four columns
    const colm = i === tx || i === tx + 2 || i === tx + 5 || i === tx + 7;
    if (y >= tip + 10 && !colm) { p.set(i, y, y > tip + 15 ? '#7a4a30' : '#2a1c2c'); continue; }
    if (y === tip + 8 && (i - tx) % 3 === 1) continue;
    p.set(i, y, pick(STONE, 0.74 - (i - tx) * 0.04, i, y, 0.6));
  }
  for (let y = tip + 3; y <= tip + 7; y++) { const hw = y - tip - 2; for (let i = -hw + 1; i <= hw; i++) p.set(tx + 3 + i, y, i <= 0 ? '#9e724a' : '#584040'); }
  p.fill(tx + 3, tip, 2, 3, '#e8c468'); p.set(tx + 3, tip, '#fff2b0'); p.set(tx + 2, tip + 1, '#c89840'); p.set(tx + 5, tip + 1, '#c89840');   // the gilt lion and lily
}
function drawTowers(p, r) {
  // San Lorenzo's dome (the Medici chapels), far behind
  for (let y = 95; y <= 112; y++) for (let i = -7; i <= 7; i++) {
    if (y < 104) { const hw = 6.5 * Math.sqrt(1 - ((103 - y) / 8.6) ** 2); if (Math.abs(i) > hw) continue; p.set(151 + i, y, pick(TILE, 0.36 - i * 0.03, 151 + i, y, 0.7)); }
    else p.set(151 + i, y, pick(MARBLE, 0.45 - i * 0.02, 151 + i, y, 0.5));
  }
  p.fill(151, 92, 1, 3, '#b8a89a');
  // the Badia's slim six-sided campanile and its spire
  for (let y = 66; y <= 120; y++) {
    const hw = y < 86 ? (y - 66) / 20 * 2.4 : 2.4;
    for (let i = -Math.round(hw); i <= Math.round(hw); i++) {
      let col = pick(STONE, (y < 86 ? 0.5 : 0.6) - i * 0.09 + (y - 66) / 110, 161 + i, y, 0.6);
      if (y >= 88 && y <= 104 && Math.abs(i) === 1 && (y - 88) % 8 < 4) col = GLASS;
      if (y === 86 || y === 95 || y === 105) col = '#e2b668';
      p.set(161 + i, y, col);
    }
  }
  p.set(161, 65, '#e2b668');
  // the Bargello: a crenellated block and its tower, the Volognana
  for (let y = 106; y <= 122; y++) for (let i = 164; i <= 179; i++) { if (y === 106 && i % 3 === 0) continue; p.set(i, y, pick(STONE, 0.34 + (y - 106) * 0.02 - (i - 164) * 0.012, i, y, 0.7)); }
  for (let y = 84; y <= 110; y++) for (let i = 170; i <= 175; i++) {
    if (y === 84 && (i & 1)) continue;
    let col = pick(STONE, 0.44 + (y - 84) * 0.012 - (i - 170) * 0.05 + (i === 170 ? 0.12 : 0), i, y, 0.7);
    if (y === 86) col = '#584040'; else if (y >= 88 && y <= 91 && (i === 172 || i === 173)) col = GLASS;
    p.set(i, y, col);
  }
  if (r() < 2) addWin(r, 167, 112, 1, 2);
}

/* ================= Santa Croce (nearer, seen from its south side) and the synagogue's green dome ================= */
function drawSantaCroce(p, r) {
  // the long nave and its aisle
  const BRICK = ['#33283c', '#463440', '#5c4244', '#74544a', '#8e6a52', '#a8825c'];                       // its plain brown flanks, not floodlit
  for (let i = 342; i <= 414; i++) for (let y = 103; y <= 130; y++) {
    const m = (i - 344) % 8, far = (i - 342) / 240;
    let col;
    if (y <= 106) col = pick(TERRA, 0.74 - (y - 103) * 0.1 + (y === 103 ? 0.2 : 0) - far, i, y, 0.7);
    else if (y <= 113) {                                                                                // clerestory: lancets between buttress strips
      col = pick(BRICK, 0.42 + (y - 107) * 0.03 - far + (m === 5 ? 0.22 : m === 6 ? -0.12 : 0), i, y, 0.7);
      if (m === 1 && y >= 108 && y <= 112) col = GLASS;
    } else if (y <= 116) col = pick(TERRA, 0.56 - (y - 114) * 0.1 + (y === 114 ? 0.18 : 0) - far, i, y, 0.7);
    else {                                                                                              // the aisle and its chapels, warmer towards the street
      col = pick(BRICK, 0.5 + (y - 117) * 0.03 - far + (m === 5 ? 0.2 : m === 6 ? -0.12 : 0), i, y, 0.7);
      if ((m === 1 || m === 2) && y >= 119 && y <= 123 && !(y === 119 && m === 2)) col = GLASS;
    }
    p.set(i, y, col);
  }
  for (let y = 99; y <= 130; y++) for (let i = 396; i <= 416; i++) {                                     // the transept's gable
    if (y < 106 && Math.abs(i - 406) > (y - 98) * 1.5) continue;
    let col = pick(BRICK, 0.5 + (y - 99) * 0.014 - (i - 396) * 0.014 + (y < 106 && Math.abs(i - 406) > (y - 99) * 1.5 ? 0.3 : 0), i, y, 0.7);
    if (Math.abs(i - 406) <= 1 && y >= 108 && y <= 116 && !(y === 108 && i !== 406)) col = GLASS;
    else if (Math.hypot(i - 406, y - 104) < 1.6) col = GLASS;
    p.set(i, y, col);
  }
  for (let y = 119; y <= 126; y++) { const hw = y < 123 ? (y - 118) * 1.2 : 5; for (let i = -Math.round(hw); i <= Math.round(hw); i++) p.set(386 + i, y, y < 123 ? pick(TERRA, 0.6 - i * 0.06, 386 + i, y) : pick(MARBLE, 0.5 - i * 0.04, 386 + i, y)); }   // the Pazzi chapel's little dome
  p.set(386, 118, '#b8a89a');
  // the marble facade's three gables, seen edge-on at the west end
  for (let i = 330; i <= 342; i++) {
    const g = Math.abs(i - 336), topY = g < 4 ? 95 + g * 2 : 104 + (g - 4);
    for (let y = topY; y <= 130; y++) {
      let col = pick(MARBLE, 0.74 - (i - 330) * 0.022 + (y - 95) * 0.006, i, y, 0.5);
      if (y === 112 || y === 122) col = GREEN_M; else if (i === 336 && y >= 100 && y <= 102) col = GLASS;
      p.set(i, y, col);
    }
  }
  for (const [sx, sy] of [[330, 102], [336, 91], [342, 102]]) { p.fill(sx, sy, 1, 4, '#f0e4c6'); p.set(sx, sy - 1, '#fff6e0'); }   // pinnacles
  // the campanile and its spire, behind the transept: pietraforte under a softer, whiter floodlight than Arnolfo's tower,
  // honey-tan rather than gold
  for (let y = 60; y <= 104; y++) {
    const hw = y < 80 ? (y - 60) / 20 * 2.6 : 2.6;
    for (let i = -Math.round(hw); i <= Math.round(hw); i++) {
      let col = pick(CROCE, 0.56 - i * 0.11 + (y - 60) / 200, 402 + i, y, 0.6);
      if (y >= 83 && y <= 88 && Math.abs(i) === 1) col = GLASS; else if (y === 80 || y === 81 || y === 91) col = '#eedcbc';
      p.set(402 + i, y, col);
    }
  }
  p.set(402, 59, '#eedcbc'); p.set(402, 58, '#c2a684');
  if (r() < 2) addWin(r, 361, 119, 2, 3);
}
function drawSynagogue(p) {
  const COPPER = ['#1e4040', '#2c5c54', '#3e7c6a', '#58a086', '#84c4a4'], cx = 449;
  for (let y = 94; y <= 104; y++) { const hw = 8 * Math.sqrt(1 - ((104 - y) / 10.5) ** 2); for (let i = Math.ceil(-hw); i <= hw; i++) p.set(cx + i, y, pick(COPPER, 0.62 - i * 0.06 - (104 - y) * 0.01, cx + i, y, 0.7)); }
  p.fill(cx, 90, 1, 4, '#c8b48c'); p.set(cx, 89, '#ffe9a0');
  for (let y = 105; y <= 122; y++) for (let i = -9; i <= 9; i++) {
    let col = pick(STONE, 0.6 - i * 0.022 + (y - 105) * 0.01, cx + i, y, 0.6);
    if (y === 105) col = '#f8d88c'; else if (y >= 108 && y <= 112 && Math.abs(i) % 4 === 1) col = GLASS; else if (y % 3 === 0) col = pick(STONE, 0.4 - i * 0.02, cx + i, y, 0.6);
    p.set(cx + i, y, col);
  }
  for (const tx of [cx - 12, cx + 12]) { p.fill(tx - 1, 104, 3, 2, '#3e7c6a'); p.set(tx, 103, '#58a086'); p.fill(tx - 1, 106, 3, 16, '#9e724a'); p.set(tx - 1, 106, '#e2b668'); }
}

/* ================= The sea of roofs: houses seen from above, row behind row down to the river ================= */
const SHUT = ['#2a4a3c', '#356048', '#447a58'];        // green shutters
function house(p, r, x, base, w, h, o = {}) {
  const top = base - h, rh = o.rh ?? Math.max(2, Math.round(h * 0.34)), tint = hash(x, base, 7), lit = o.lit ?? 0.26;
  const fade = col => (o.fade ? mixHex(col, '#4c4c7c', o.fade) : col);                 // far rows sink into the evening's blue
  for (let y = top + rh; y <= base; y++) for (let i = x; i < x + w; i++) {
    let v = lit + (y - top) / h * 0.3 + (tint - 0.5) * 0.3;
    if (i === x + w - 1) v -= 0.2; else if (i === x) v += 0.08;
    if (o.ground && y > base - o.ground) v -= 0.16;
    p.set(i, y, fade(pick(WALL, v, i, y, 0.6)));
  }
  const inset = r() < 0.45 ? 1 : 0, rv = 0.28 + r() * 0.34;
  for (let y = top; y < top + rh; y++) {
    const k = y - top, a = x - 1 + (k === 0 ? inset : 0), b = x + w - (k === 0 ? inset : 0);
    for (let i = a; i <= b; i++) p.set(i, y, fade(pick(TERRA, rv + (k === 0 ? 0.24 : 0) - (k === rh - 1 && rh > 2 ? 0.18 : 0) - (i - x) / w * 0.12, i, y, 0.8)));
  }
  if (r() < 0.3) { const cx = x + 1 + Math.floor(r() * Math.max(1, w - 2)); p.set(cx, top - 1, '#54424c'); if (h > 8) p.set(cx, top - 2, '#705850'); }
  const ww = o.ww || 1, wh = o.wh || 1, sx = ww + (o.shut ? 3 : 2), sy = wh + 2;
  for (let wy = top + rh + 1; wy + wh <= base - (o.ground || 0); wy += sy) for (let wx = x + (o.shut ? 2 : 1); wx + ww <= x + w - 1; wx += sx) {
    const q = r();
    if (q < 0.16) continue;
    if (o.shut) { const sc = SHUT[Math.floor(r() * 3)]; p.fill(wx - 1, wy, 1, wh, sc); p.fill(wx + ww, wy, 1, wh, sc); }
    p.fill(wx, wy, ww, wh, q < 0.56 ? GLASS : LIT[Math.floor(r() * 4)]);
    if (q >= 0.56 && q < 0.8) { p.fill(wx, wy, ww, wh, GLASS); addWin(r, wx, wy, ww, wh, !!o.under); }
  }
  if (o.ground) for (let wx = x + 1; wx + 1 < x + w - 1; wx += 4) if (r() < 0.6) p.fill(wx, base - o.ground + 1, 2, o.ground - 1, r() < 0.5 ? '#e89a48' : '#2a2238');   // doors and shop fronts
}
const NEAR_BASE = x => (x < 84 ? 133 : bankY(x) - 4);  // (left of Ponte Vecchio the far river shows over the bridge)
const rowBase = (k, x) => Math.round(112 + (NEAR_BASE(x) - 112) * Math.pow(k / 8, 0.9));
function drawRow(p, r, k) {
  let x = -6 + Math.floor(r() * 5);
  while (x < W + 4) {
    const w = Math.round(5 + k * 0.9 + r() * (4 + k * 0.7)), xm = x + (w >> 1), base = rowBase(k, xm);
    let h = Math.round(4 + k * 0.9 + r() * (2 + k * 0.6));
    if (k >= 6 && xm > 196 && xm < 280) h = Math.min(h, base - 152);                    // keep the hoarding clear
    if (xm < 84) h = Math.min(h, 3 + k);
    if (h >= 3) house(p, r, x, base, w, h, { wh: k > 3 && xm >= 84 ? 2 : 1, lit: 0.2 + k * 0.012, fade: Math.round((0.4 - k * 0.05) * 20) / 20 });
    x += w;
    if (r() < 0.3 && h >= 3) {                                                          // a lane: a lamp glows in the gap
      const g = 1 + Math.floor(r() * 2);
      p.fill(x, base - 2, g, 3, '#2a2238'); p.set(x, base, '#c8783c');
      if (base > 114) TWINK.push({ x, y: base - 1, k: 40 + Math.floor(r() * 90), ph: r() });
      x += g;
    }
  }
}

/* ================= The far bank: the lungarno's houses, the National Library, the Uffizi's river front ================= */
const QUAY_LAMPS = [];                                 // lamps on the river wall: [x, y]
function drawLungarno(p, r) {
  let x = 100;
  while (x < W + 4) {
    const near = x / W, w = Math.round(8 + near * 5 + r() * 6), xm = x + (w >> 1), base = bankY(Math.min(479, xm)) - 3;
    let h = Math.round(11 + near * 5 + r() * 4);
    if (xm > 196 && xm < 280) h = Math.min(h, base - 152);
    if (x + w > 332 && x < 378) { x = 378; continue; }                                  // (the library stands here)
    house(p, r, x, base, w, h, { ww: near > 0.55 ? 2 : 1, wh: near > 0.3 ? 2 : 2, shut: near > 0.55, lit: 0.42, ground: 3, rh: 2 });
    x += w;
  }
  // the Biblioteca Nazionale: pale stone, two little towers, a skylight dome
  const BN = ['#4a4258', '#6a5e6c', '#8c7c78', '#ac9a84', '#c8b490', '#e0cca0'], b0 = bankY(355) - 3;
  for (let y = b0 - 17; y <= b0 + 1; y++) for (let i = 334; i <= 376; i++) {
    let col = pick(BN, 0.5 + (y - b0 + 17) * 0.02 - (i - 334) * 0.004, i, y, 0.6);
    if (y === b0 - 17) col = '#e0cca0'; else if ((y === b0 - 13 || y === b0 - 8) && (i - 336) % 4 < 2) col = GLASS;
    else if (y > b0 - 5 && (i - 336) % 6 < 3 && y < b0) col = y === b0 - 4 && (i - 336) % 6 !== 1 ? col : '#3a2c3c';
    p.set(i, y, col);
  }
  for (const tx of [337, 369]) {
    for (let y = b0 - 27; y < b0 - 17; y++) for (let i = tx; i < tx + 5; i++) p.set(i, y, y === b0 - 27 ? '#e0cca0' : i === tx + 2 && y > b0 - 25 && y < b0 - 20 ? GLASS : pick(BN, 0.6 - (i - tx) * 0.07, i, y, 0.6));
    p.fill(tx + 1, b0 - 29, 3, 2, '#8c7c78'); p.set(tx + 2, b0 - 30, '#c8b490');
  }
  for (let y = b0 - 21; y < b0 - 17; y++) { const hw = 3 - (b0 - 18 - y); for (let i = -hw; i <= hw; i++) p.set(355 + i, y, i < 0 ? '#7a9a9a' : '#4a6a72'); }
  for (let i = 344; i < 368; i += 4) if (r() < 0.6) addWin(r, i, b0 - 13, 2, 1);
  // the Uffizi's short river front: three storeys of grey stone over the loggia's arch
  const UF = ['#3e3a50', '#5a5462', '#7c7478', '#a0968c', '#c2b6a0'];
  for (let y = 131; y <= 152; y++) for (let i = 85; i <= 99; i++) {
    let col = pick(UF, 0.5 + (y - 131) * 0.016 - (i - 85) * 0.012, i, y, 0.6);
    if (y === 131) col = pick(TERRA, 0.7, i, y); else if (y === 132 || y === 141) col = '#c2b6a0';
    else if ((y === 135 || y === 136) && (i - 86) % 3 === 0) col = GLASS;
    else if (y >= 144 && ((Math.abs(i - 92) <= 2 && !(y === 144 && Math.abs(i - 92) === 2)) || ((i === 87 || i === 97) && y >= 146))) col = y > 148 ? '#e89a48' : '#ffd98c';
    p.set(i, y, col);
  }
  for (let i = 80; i < 85; i++) for (let y = 143; y <= 152; y++) p.set(i, y, y < 146 ? '#8e7258' : (i & 1) ? '#3a2c3c' : '#705850');   // the corridor's arches along the lungarno
  // the river wall and its lamps
  for (let i = 80; i < W; i++) {
    const y = bankY(i);
    p.set(i, y - 2, i % 13 === 0 ? '#8e8478' : '#b0a494'); p.set(i, y - 1, i % 13 === 0 ? '#54485a' : '#6e6470');
    if (i % 22 === 8 && i > 100) { p.set(i, y - 3, '#3a3048'); p.set(i, y - 4, '#3a3048'); p.set(i, y - 5, '#fff0c0'); QUAY_LAMPS.push([i, y - 5]); }
  }
}

/* ================= Ponte Vecchio: three arches, the shops hanging over the water, the corridor on top ================= */
const SHOP_COLS = ['#d8a050', '#e8c070', '#c87840', '#e0b890', '#b86a48', '#f0d090', '#d89060'];
const FAR_RIVER = ['#8a6c8c', '#b07c88', '#d0907e', '#eaa678', '#f8c084'];   // the Arno beyond the bridges, still full of the west
const PV_ARCHES = [[16, 34, 151], [38, 58, 150], [62, 79, 151]];   // [from, to, crown row]: the three segmental spans
const PV_PIERS = [[35, 37], [59, 61]];
const PV_LOGGIA = [44, 53];                            // the open loggia under the corridor at the bridge's middle
function drawPonteVecchio(p, r) {
  const { x0, x1 } = PONTE;
  // the far river over the bridge's roofs, crossed by Ponte alla Carraia and Ponte Vespucci; Santa Trinita's lamps
  for (let i = x0 + 2; i <= x1 - 2; i++) for (let y = 133; y <= 138; y++) {
    let col = pick(FAR_RIVER, 0.9 - Math.abs(i - 44) / 60 - (138 - y) * 0.04 + (hash(i, y, 3) - 0.5) * 0.2, i, y, 0.8);
    if (y === 134 && i > x0 + 8 && i < x1 - 8) col = '#4a3c58'; else if (y === 136) col = '#3e3250';
    p.set(i, y, col);
  }
  for (let i = x0 + 6; i < x1 - 4; i += 9) { TWINK.push({ x: i, y: 135, k: 50 + i, ph: i * 0.37 % 1, keep: true }); TWINK.push({ x: i + 4, y: 138, k: 70 + i, ph: i * 0.61 % 1, keep: true }); }
  // the Mannelli tower at the south end, and the first Oltrarno houses
  for (let y = 132; y <= 157; y++) for (let i = 6; i <= 15; i++) p.set(i, y, y === 132 && (i & 1) ? '#2a2238' : pick(STONE, 0.3 + (y - 132) * 0.012 - (i - 6) * 0.02, i, y, 0.7));
  house(p, r, -2, 157, 9, 26, { lit: 0.2, wh: 2 });
  p.fill(10, 138, 2, 3, GLASS); addWin(r, 10, 146, 2, 2);
  // three segmental arches on two piers, the central one the widest and highest: the floodlit voussoirs ring each
  // opening, a line of shadow under the vault, the lit river beyond; each pier's cutwater points at us at the waterline
  for (let i = x0; i <= x1; i++) {
    const wl = bankY(i), a = PV_ARCHES.find(([s, e]) => i >= s && i <= e);
    let yi = wl, mid = 0, half = 0;                                                     // yi: the arch's underside here
    if (a) {
      const [s, e, crown] = a, rise = bankY(Math.round((s + e) / 2)) - crown;
      mid = (s + e) / 2; half = (e - s + 1) / 2;
      const rad = (half * half + rise * rise) / (2 * rise), dx = i - mid;
      yi = Math.round(crown + rad - Math.sqrt(Math.max(0, rad * rad - dx * dx)));
    }
    for (let y = 148; y < wl; y++) {
      let col;
      if (y < yi - 1) col = pick(STONE, 0.24 + (hash(i, y, 5) - 0.5) * 0.12, i, y, 0.7);                       // spandrels and piers
      else if (y === yi - 1) col = i < mid ? '#e2b26a' : '#b8864e';                                            // the arch ring
      else if (y - yi < (Math.abs(i - mid) < half * 0.6 ? 2 : 1) && Math.abs(i - mid) < half - 1) col = y === yi ? '#2a1a2a' : '#4a2c3a';   // under the vault
      else col = pick(FAR_RIVER, 0.3 + (y - yi) * 0.13, i, y, 0.8);
      p.set(i, y, col);
    }
  }
  for (const [s, e] of PV_PIERS) {
    const pc = (s + e) / 2, wl = bankY(Math.round(pc));
    for (let y = 149; y < wl; y++) { p.set(s, y, pick(STONE, 0.56, s, y, 0.6)); p.set(e, y, pick(STONE, 0.16, e, y, 0.6)); }
    for (let k = 0; k < 3; k++) for (let i = Math.floor(pc - k - 1); i <= Math.ceil(pc + k + 1); i++)               // the cutwater
      p.set(i, wl - 3 + k, i < pc ? '#c29254' : i > pc ? '#6a4840' : '#e2b668');
  }
  // the botteghe: plastered boxes of every height hanging out over the river on wooden brackets, shutters and lit
  // windows; in the middle the corridor rides over an open loggia of three arches
  const [la, lb] = PV_LOGGIA;
  for (const [sa, sb] of [[x0, la - 1], [lb + 1, x1]]) for (let x = sa; x <= sb;) {
    const w = Math.min(sb - x + 1, 4 + Math.floor(r() * 3)), top = 144 + (r() < 0.4 ? 1 : 0), drop = 147 + Math.floor(r() * 2);
    const col = mixHex(SHOP_COLS[Math.floor(r() * SHOP_COLS.length)], '#4a3050', 0.18 + r() * 0.16);
    for (let y = top; y <= drop; y++) for (let i = x; i < x + w; i++)
      p.set(i, y, y === top && top > 144 ? pick(TERRA, 0.5, i, y) : i === x + w - 1 ? mixHex(col, '#3a2a3a', 0.45) : i === x ? mixHex(col, '#fff0d0', 0.18) : col);
    if (top > 144) for (let i = x; i < x + w; i++) p.set(i, 144, '#4a3040');               // the corridor's shadow over a lower roof
    const lit = r() < 0.6, wy = drop - 2;
    p.fill(x + 1, wy, 1, 2, lit ? '#ffe2a0' : GLASS); if (w > 4) p.fill(x + 3, wy, 1, 2, r() < 0.5 ? '#ffd080' : '#5a3a2c');
    if (r() < 0.5) addWin(r, x + 1, wy, 1, 2);
    for (let i = x; i < x + w; i++) p.set(i, drop + 1, '#2a1e2c');                          // the shadow under the box
    p.set(x, drop + 2, '#4a3428'); p.set(x + w - 1, drop + 2, '#4a3428');                   // brackets
    x += w;
  }
  for (let i = la; i <= lb; i++) for (let y = 144; y <= 148; y++) {
    const pier = (i - la) % 3 === 0;
    let col = pier ? (y === 144 ? '#9e724a' : '#c29254') : y === 144 ? '#9e724a' : y === 145 ? '#3a2434' : y === 148 ? '#b08a60' : pick(FAR_RIVER, 0.8, i, y);
    if (pier && y === 148) col = '#b08a60';
    p.set(i, y, col);
  }
  // the corridor: a tiled roof, a lit eave, a row of small windows (three big ones over the loggia), a corbel line
  for (let i = x0 - 2; i <= x1 + 2; i++) {
    const big = i >= la + 1 && i <= lb - 1 && (i - la) % 3 !== 0, win = big || (i - x0) % 3 === 1;
    p.set(i, 139, pick(TERRA, 0.62 - (i - x0) / 200, i, 139)); p.set(i, 140, '#c09a6c');
    p.set(i, 141, win ? GLASS : '#b08a60'); p.set(i, 142, win ? (big ? GLASS : '#3a3048') : '#a07c58');
    p.set(i, 143, '#5a4040');
    if (win && !big && r() < 0.35) addWin(r, i, 141, 1, 1);
  }
}

/* ================= The palazzo below the cathedral, its scaffold and the hoarding (the city's one billboard) ================= */
const PAL = { x0: 208, x1: 268, top: 124, base: 155 };
function renderPalazzo() {                             // what the loop videos show: the house without its scaffold
  const p = pix(W, H), r = rng(404), { x0, x1, top, base } = PAL;
  WIN_LAYER = 2;
  for (let y = top; y <= base; y++) for (let i = x0 - 1; i <= x1 + 1; i++) {
    let col;
    if (y < top + 3) col = pick(TERRA, 0.6 - (y - top) * 0.14 - (i - x0) / 200, i, y, 0.8);
    else if (i < x0 || i > x1) continue;
    else {
      col = pick(WALL, 0.56 + (y - top) * 0.01 - (i - x0) * 0.004 + (vnoise(i / 3, y / 3, 17) - 0.5) * 0.14, i, y, 0.6);
      if (y === top + 3) col = '#c8a672'; else if (y === top + 13 || y === top + 23) col = '#8e7258';
      else if (y > top + 23 && (y & 1)) col = pick(STONE, 0.34 - (i - x0) * 0.003, i, y, 0.6);       // rusticated ground floor
    }
    p.set(i, y, col);
  }
  for (const wy of [top + 6, top + 16]) for (let i = x0 + 4; i < x1 - 3; i += 8) {
    p.set(i + 1, wy - 1, '#c8a672'); p.fill(i, wy, 3, 5, GLASS); p.fill(i - 1, wy, 1, 5, SHUT[1]); p.fill(i + 3, wy, 1, 5, SHUT[0]);
    if (r() < 0.75) addWin(r, i, wy, 3, 5, true);
  }
  for (let i = x0 + 5; i < x1 - 4; i += 12) p.fill(i, top + 26, 3, 5, '#2a2238');
  return trim(p);
}
function renderScaffold() {
  const p = pix(W, H), X0 = PAL.x0 - 2, X1 = PAL.x1 + 2, T = BILL.y - 6, B = PAL.base;
  const NET = ['#3c4a50', '#506664', '#68827a', '#86a090', '#a4baa4'];
  for (let y = T + 2; y <= B; y++) for (let i = X0; i <= X1; i++)                                     // the debris net, in soft folds
    p.set(i, y, pick(NET, 0.46 + 0.2 * Math.sin(i * 0.7 + y * 0.12) + (y - T) * 0.004 - (i - X0) * 0.003, i, y, 0.7));
  for (let i = X0; i <= X1; i += 8) for (let y = T - 2; y <= B; y++) p.set(i, y, (y & 1) ? '#22202e' : '#3a3648');   // standards
  for (let y = T + 2; y <= B; y += 7) for (let i = X0; i <= X1; i++) if (!p.alpha(i, y) || (i & 1)) p.set(i, y, '#2a2636');   // ledgers and boards
  for (let i = X0; i <= X1; i++) p.set(i, T + 1, '#4a4658');
  // the hoarding: a dark frame round the face, three lamps on arms above it
  p.fill(BILL.x - 2, BILL.y - 2, BILL.w + 4, BILL.h + 4, '#1a1826'); p.fill(BILL.x - 2, BILL.y - 2, BILL.w + 4, 1, '#565068');
  p.fill(BILL.x - 1, BILL.y - 1, BILL.w + 2, BILL.h + 2, '#6e5a48'); p.fill(BILL.x, BILL.y, BILL.w, BILL.h, '#0d0f24');
  for (const lx of BILL_LAMPS) { p.fill(lx, BILL_LAMP_Y + 1, 1, 2, '#22202e'); p.fill(lx - 1, BILL_LAMP_Y - 1, 3, 1, '#22202e'); p.fill(lx - 1, BILL_LAMP_Y, 3, 1, '#ffe8b0'); }
  return trim(p);
}

/* ================= Assembling the city: behind the hoarding (back) and in front of it (front) ================= */
function renderCity() {
  const back = pix(W, H), front = pix(W, H), r = rng(2026);
  WIN_LAYER = 0;
  drawRow(back, r, 0); drawRow(back, r, 1);
  drawSynagogue(back); drawDuomo(back); drawCampanile(back);
  drawRow(back, r, 2); drawTowers(back, r);
  drawRow(back, r, 3); drawPalazzoVecchio(back, r);
  drawRow(back, r, 4); drawSantaCroce(back, r);
  drawRow(back, r, 5);
  WIN_LAYER = 1;
  const twBack = TWINK.length;
  drawRow(front, r, 6); drawRow(front, r, 7);
  drawLungarno(front, r); drawPonteVecchio(front, r);
  // keep only the windows and lane lamps that nothing painted later covers
  const LANE = C('#c8783c');
  const hidden = (x, y) => front.alpha(x, y) > 0 || (x >= PAL.x0 - 3 && x <= PAL.x1 + 3 && y >= BILL.y - 8 && y <= PAL.base);
  const keepW = WINS.filter(w => (w.layer === 1 ? isGlass(front, w) : isGlass(back, w) && !hidden(w.x, w.y)));
  WINS.length = 0; WINS.push(...keepW);
  const keepT = TWINK.filter((q, i) => q.x >= 0 && q.x < W && (q.keep || (i >= twBack ? isCol(front, q.x, q.y + 1, LANE) : isCol(back, q.x, q.y + 1, LANE) && !hidden(q.x, q.y))));
  TWINK.length = 0; TWINK.push(...keepT);
  return { back, front };
}

/* ================= The Arno, kept in "bank space": row k is k px below the far bank, whatever the bank's slope ================= */
const WATER = ['#141c40', '#1c2850', '#28386a', '#3a4a80', '#58608e', '#806e94', '#a87c92', '#cc8e88', '#e8a47c'];
const waterV = (x, k) => 0.25 + 0.65 * Math.pow(clamp(1 - (x - 40) / 330, 0, 1), 1.2) - k * 0.004;
function renderWater() {
  const p = pix(W, WDEPTH);
  for (let k = 0; k < WDEPTH; k++) for (let x = 0; x < W; x++) p.set(x, k, pick(WATER, waterV(x, k) + (hash(k, 3, 31) - 0.5) * 0.09 + (vnoise(x / 26, k / 1.5, 31) - 0.5) * 0.1, x, k, 0.3));   // row by row, so the tones meet in ripples, not in columns
  return p.done();
}
/** the far bank's houses, upside down, row by row below the bank (the mirror only shows what stands at the water) */
function renderMirror(front) {
  const p = pix(W, 24);
  for (let k = 0; k < 24; k++) for (let x = 0; x < W; x++) {
    const sy = bankY(x) - 1 - Math.floor(k * 1.4), o = (sy * W + x) * 4;
    if (sy < 0 || !front.d[o + 3]) continue;
    p.set(x, k, [front.d[o], front.d[o + 1], front.d[o + 2]]);
  }
  return p.done();
}
const STRIPS = [];                                     // runs of columns that share a bank row: the water is laid down strip by strip
for (let x = 0, x0 = 0; x <= W; x++) if (x === W || bankY(x) !== bankY(x0)) { STRIPS.push({ x: x0, w: x - x0, y: bankY(x0) }); x0 = x; }
const weirK = x => (WEIR.x1 - x) / (WEIR.x1 - WEIR.x0) * 27;   // the weir's crest in bank space

/* ================= Ponte alle Grazie: five flat arches on slim piers, nearly side-on from up here ================= */
const grazieY = x => GRAZIE.y0 + (x - GRAZIE.x0) * (GRAZIE.y1 - GRAZIE.y0) / (GRAZIE.x1 - GRAZIE.x0);   // its waterline
const GRAZIE_LAMPS = [];
function renderGrazie() {
  const p = pix(W, H), n = 5, span = (GRAZIE.x1 - GRAZIE.x0) / n;
  for (let i = GRAZIE.x0 - 3; i <= GRAZIE.x1 + 4; i++) {
    const wl = Math.round(grazieY(i)), k = (i - GRAZIE.x0) / span, u = (k - Math.floor(k)) * 2 - 1, inside = i >= GRAZIE.x0 && i <= GRAZIE.x1;
    const pier = inside && Math.abs(Math.abs(u) - 1) * span / 2 < 1.6 && k > 0.2 && k < n - 0.2;
    p.set(i, wl - 8, '#b8a890'); p.set(i, wl - 7, i % 3 ? '#5a5060' : '#8a7c7c');                     // parapet
    p.set(i, wl - 6, '#a4947c'); p.set(i, wl - 5, '#7a6a64');                                         // deck
    if (!inside) { for (let y = wl - 4; y <= wl; y++) p.set(i, y, '#5e5058'); continue; }
    const top = wl - 4 + Math.round(2.4 * u * u);
    for (let y = wl - 4; y <= wl; y++) if (pier) p.set(i, y, y === wl ? '#3e3648' : pick(STONE, 0.42 - u * 0.1, i, y, 0.6)); else if (y < top) p.set(i, y, '#4e4452');
    if (pier) p.set(i, wl + 1, '#2c2840');
  }
  for (let k = 0; k <= 6; k++) { const x = Math.round(GRAZIE.x0 + 4 + k * (GRAZIE.x1 - GRAZIE.x0 - 8) / 6), y = Math.round(grazieY(x)) - 10; p.set(x, y + 1, '#3a3048'); p.set(x, y, '#fff0c0'); GRAZIE_LAMPS.push([x, y]); }
  return trim(p);
}

/* ================= The near bank: San Niccolo's roofs from behind, plane trees on the lungarno, the wooded slope ================= */
const SOUTH_LAMPS = [], FIREFLY_HOMES = [];
function renderSouth() {
  const p = pix(W, H), r = rng(77), mark = WINS.length;
  WIN_LAYER = 3;
  const TREE = ['#0d1426', '#131c30', '#1a2838', '#243640', '#34483f'];
  // houses, row behind row, the first row's roofs making the bank's outline against the water
  for (let j = 0; j < 6; j++) {
    let x = -8 + Math.floor(r() * 6);
    while (x < 250) {
      const w = 11 + Math.floor(r() * 10), xm = x + (w >> 1), h = 8 + Math.floor(r() * 5) + j, top = southY(xm) + j * 7 + Math.floor(r() * 3) - 1;
      if (top + h < RAIL + 12) house(p, r, x, top + h, w, h, { lit: 0.08, wh: 2, ww: j > 1 ? 2 : 1, rh: 3 + (j >> 1) });
      x += w + (r() < 0.35 ? 2 : 0);
      if (r() < 0.2 && top + h < RAIL - 4) SOUTH_LAMPS.push([x - 1, top + h - 2]);
    }
  }
  for (let i = 0; i < 250; i++) for (let y = southY(i) + 4; y < RAIL + 14; y++) if (!p.alpha(i, y)) p.set(i, y, '#241c30');   // lanes
  for (const [x, y] of SOUTH_LAMPS) { p.set(x, y, '#ffe2a0'); p.set(x, y + 1, '#c8783c'); }
  // plane trees along Lungarno Serristori, at the water's edge
  for (let x = 66; x < 236; x += 9 + Math.floor(r() * 5)) {
    const cy = southY(x) + 1, rr = 3 + Math.floor(r() * 2);
    for (let dy = -rr; dy <= rr; dy++) for (let dx = -rr - 1; dx <= rr + 1; dx++) {
      const v = 1 - (dx * dx) / ((rr + 1.5) ** 2) - (dy * dy) / (rr * rr) + (r() - 0.5) * 0.3;
      if (v > 0) p.set(x + dx, cy + dy, pick(TREE, 0.3 + v * 0.3 - dy * 0.08, x + dx, cy + dy));
    }
  }
  // the slope right under the terrace: holm oaks and pines in one dark mass, cypresses standing out of it
  const massY = x => 189 - 5 * Math.sin(x * 0.05 + 1) - 3 * Math.sin(x * 0.13) + (x > 150 ? (x - 150) * 0.1 : 0) - Math.max(0, 60 - x) * 0.25;
  for (let i = 0; i < 262; i++) for (let y = Math.round(massY(i) + (vnoise(i / 3, 0, 41) - 0.5) * 5); y < RAIL + 14; y++)
    p.set(i, y, pick(TREE, 0.2 + (vnoise(i / 4, y / 3, 43) - 0.5) * 0.7 + (y < massY(i) + 2 ? 0.3 : 0), i, y));
  for (const [cx, top] of [[36, 164], [44, 170], [142, 176], [150, 171], [158, 178], [228, 180], [236, 185], [248, 188]]) {
    for (let y = top; y < RAIL + 14; y++) { const hw = Math.min(3, 0.6 + (y - top) * 0.16); for (let i = Math.round(-hw); i <= Math.round(hw); i++) p.set(cx + i, y, i < 0 ? '#1a2838' : '#0d1426'); }
    FIREFLY_HOMES.push([cx - 5, top + 12], [cx + 6, top + 16]);
  }
  for (let x = 6; x < 256; x += 13) FIREFLY_HOMES.push([x + Math.floor(r() * 8), Math.round(massY(x)) + 2 + Math.floor(r() * 5)]);
  const keep = WINS.slice(mark).filter(w => w.y + w.h < RAIL - 1 && isGlass(p, w));                   // (the trees hide some windows)
  WINS.length = mark; WINS.push(...keep);
  return trim(p);
}
/* the old gate tower of San Niccolo: three great arches stacked on the side towards the hill, floodlit */
function renderTower() {
  const p = pix(W, H), { x0, x1, top } = TOWER, cx = (x0 + x1) >> 1;
  for (let y = top; y < RAIL + 12; y++) for (let i = x0 - 1; i <= x1 + 1; i++) {
    const gal = y < top + 8;
    if (!gal && (i < x0 || i > x1)) continue;
    if (y < top + 2 && (i - x0 + 1) % 4 >= 2) continue;                                               // merlons
    let col = pick(STONE, 0.5 + (y - top) / 60 * 0.4 - (i - x0) * 0.014 + (vnoise(i / 2, y / 2, 61) - 0.5) * 0.14 + (i <= x0 ? 0.1 : 0), i, y, 0.8);
    if (gal && y === top + 7) col = (i & 1) ? '#f8d88c' : '#3a2a3a';                                  // corbels
    for (const a of [top + 12, top + 24, top + 36]) {
      const k = y - a;
      if (k >= 0 && k < 9 && Math.abs(i - cx) <= 3 && !(k === 0 && Math.abs(i - cx) > 1) && !(k === 1 && Math.abs(i - cx) > 2)) col = k > 5 ? '#b8703c' : k > 3 ? '#6a3c30' : '#2a1c2c';
    }
    p.set(i, y, col);
  }
  return trim(p);
}

/* ================= The foreground: the little terrace and its balustrade, the steps, the lamps, the coffee three-wheeler ================= */
const PAVE = ['#1b1d3a', '#262844', '#35324e', '#4a3e50', '#644e50', '#826654', '#a2845e', '#bea06c', '#d8ba80'];   // moonless stone to lamplit stone
const IRON = { d: '#0e1018', m: '#1a1e2a', h: '#3a4250', w: '#8a6a44' };
const LANTERNS = LAMPS.flatMap(x => [[x, LAMP_Y - 5], [x - 6, LAMP_Y + 3], [x + 6, LAMP_Y + 3]]);   // three to a post
const PIERS = [39, 88, 137, 186, 235, 284, 333, 382, 431];
const BLOOMS = [];                                     // the rose hedges' flowers: they nod in the breeze, so they are drawn per frame
/** how much lamplight falls on the ground at (x, y): the two lamps and the cart's hatch */
function lampLight(x, y) {
  let v = 0;
  for (const lx of LAMPS) v += Math.max(0, 1 - Math.hypot((x - lx) / 116, (y - 210) / 84)) ** 1.25 * 0.9;
  v += Math.max(0, 1 - Math.hypot((x - 114) / 46, (y - 213) / 20)) * 0.42;
  return Math.min(1, v);
}
const sideL = y => 40 - (y - FOOT) * 0.6, sideR = y => 440 + (y - FOOT) * 0.6;   // the steps' side walls open towards us
function renderFore() {
  const p = pix(W, H), r = rng(808);
  // the terrace's paving
  for (let y = FLOOR; y < FOOT; y++) for (let i = 0; i < W; i++) {
    let v = 0.14 + lampLight(i, y) * 0.8 + (hash(i >> 3, y >> 1, 3) - 0.5) * 0.06;
    if ((i + (y & 2) * 5) % 13 === 0) v -= 0.08;
    p.set(i, y, pick(PAVE, v, i, y, 0.8));
  }
  // the steps, seen from the top: each tread a band, its lit nosing along the far edge
  const edges = [FOOT, ...STEPS, H + 8];
  for (let k = 0; k + 1 < edges.length; k++) for (let y = edges[k]; y < Math.min(H, edges[k + 1]); y++) for (let i = 0; i < W; i++) {
    const d = y - edges[k], span = edges[k + 1] - edges[k];
    let v = 0.16 + lampLight(i, y) * 0.7 - d / span * 0.2 + (hash(i >> 2, y, 5) - 0.5) * 0.05;
    if (d === 0) v += 0.26; else if (d === 1) v += 0.1; else if (d === span - 1) v -= 0.1;   // the nosing; the shade at the next riser's foot
    if (d > 0 && (i + k * 17) % (24 + k * 4) === 0) v -= 0.1;                     // joints
    p.set(i, y, pick(PAVE, v, i, y, 0.8));
  }
  // side walls with rose hedges behind their copings
  const ROSE_LEAF = ['#0e1c1e', '#152a26', '#1e3a2e', '#2c5038'], BLOOM = ['#d16177', '#e88aa0', '#f4c0c8', '#b83a52', '#f8e4dc'];
  for (let y = FOOT - 6; y < H; y++) for (const side of [0, 1]) {
    const e = side ? sideR(Math.max(FOOT, y)) : sideL(Math.max(FOOT, y));
    for (let i = side ? Math.round(e) : 0; side ? i < W : i <= Math.round(e); i++) {
      const d = side ? i - e : e - i;
      if (y < FOOT && d < 3) continue;
      let col;
      if (d < 3) col = pick(PAVE, (d < 1 ? 0.5 : 0.3) + lampLight(i, y) * 0.5, i, y, 0.8);                          // coping
      else col = pick(ROSE_LEAF, 0.3 + (vnoise(i / 3, y / 3, 81) - 0.5) * 0.9 + lampLight(i, y) * 0.3, i, y);
      p.set(i, y, col);
      if (d >= 4 && hash(i, y, 83) < 0.06) BLOOMS.push({ x: i, y, col: mixHex(BLOOM[Math.floor(hash(i, y, 84) * BLOOM.length)], '#1e1830', 0.5 - lampLight(i, y) * 0.4), two: hash(i, y, 85) < 0.5, ph: hash(i >> 4, y >> 4, 86) });
    }
  }
  // the balustrade: rail, turned balusters with the view between them, plinth; a pier every 49 px
  for (let i = 0; i < W; i++) {
    const L = lampLight(i, 204), pier = PIERS.some(q => Math.abs(i - q) <= 3), cap = PIERS.some(q => Math.abs(i - q) <= 4);
    if (cap) { p.set(i, RAIL - 2, pick(PAVE, 0.5 + L * 0.5, i, RAIL - 2)); p.set(i, RAIL - 1, pick(PAVE, 0.3 + L * 0.4, i, RAIL - 1)); }
    p.set(i, RAIL, pick(PAVE, 0.48 + L * 0.5, i, RAIL, 0.6)); p.set(i, RAIL + 1, pick(PAVE, 0.24 + L * 0.4, i, RAIL + 1, 0.6));
    for (let y = RAIL + 2; y <= RAIL + 7; y++) {
      const m = i % 4, bulge = y === RAIL + 4 || y === RAIL + 5;
      if (pier) p.set(i, y, pick(PAVE, 0.3 + L * 0.5 - (PIERS.some(q => i - q === 3) ? 0.15 : 0), i, y, 0.7));
      else if (m === 1 || (bulge && m === 2)) p.set(i, y, pick(PAVE, (m === 1 ? 0.34 : 0.16) + L * 0.5, i, y, 0.7));
    }
    p.set(i, RAIL + 8, pick(PAVE, 0.36 + L * 0.5, i, RAIL + 8, 0.6)); p.set(i, RAIL + 9, pick(PAVE, 0.16 + L * 0.4, i, RAIL + 9, 0.6));
  }
  // the cast-iron lamps on their piers: a fluted post, a collar, two scroll arms and three lanterns
  for (const X of LAMPS) {
    for (let y = LAMP_Y + 2; y < RAIL - 2; y++) { p.set(X, y, y > RAIL - 14 ? IRON.m : IRON.d); p.set(X + 1, y, IRON.d); if (y > RAIL - 12) p.set(X - 1, y, IRON.h); else if ((y & 3) === 0) p.set(X, y, IRON.h); }
    p.fill(X - 2, RAIL - 5, 5, 3, IRON.d); p.fill(X - 2, RAIL - 5, 5, 1, IRON.w); p.fill(X - 1, RAIL - 16, 3, 2, IRON.d); p.set(X - 1, RAIL - 16, IRON.w);
    for (const s of [-1, 1]) {
      for (let k = 1; k <= 6; k++) p.set(X + s * k, LAMP_Y + 9 - Math.round(Math.sin(k / 6 * Math.PI / 2) * 3) + (k < 3 ? 1 : 0), IRON.d);
      p.set(X + s * 3, LAMP_Y + 10, IRON.d); p.set(X + s * 4, LAMP_Y + 10, IRON.d);
    }
    for (const [lx, ly] of LANTERNS.filter(q => Math.abs(q[0] - X) <= 6)) {
      p.fill(lx - 1, ly - 3, 3, 1, IRON.d); p.set(lx, ly - 4, IRON.d); p.set(lx, ly - 5, IRON.w);                 // cap and finial
      p.fill(lx - 2, ly - 2, 5, 1, IRON.d);
      p.fill(lx - 1, ly - 1, 3, 4, '#ffe9b4'); p.fill(lx - 1, ly - 1, 3, 1, '#fff8e0'); p.set(lx, ly + 2, '#ffc468');
      p.set(lx - 2, ly - 1, IRON.d); p.set(lx + 2, ly - 1, IRON.d); p.set(lx - 2, ly + 2, IRON.d); p.set(lx + 2, ly + 2, IRON.d);
      p.fill(lx - 1, ly + 3, 3, 1, IRON.d);
    }
  }
  return trim(p);
}
/* the coffee three-wheeler: a little van with its side hatch up, a striped flap, a board on the roof */
const SAGE = ['#2e4640', '#456658', '#618a70', '#86ac8a'], CREAM = '#ecdcb8';
function renderCart() {
  const p = pix(W, H), X = CART.x, Y = CART.y;
  for (const wx of [X + 3, X + 29]) { p.fill(wx, Y + 17, 4, 4, '#12121a'); p.fill(wx + 1, Y + 18, 2, 2, '#5a5a68'); p.clear(wx, Y + 17); p.clear(wx + 3, Y + 17); p.clear(wx, Y + 20); p.clear(wx + 3, Y + 20); }
  for (let y = Y + 5; y <= Y + 18; y++) for (let i = X; i <= X + 11; i++) {                               // the cab, nose to the left
    if (y < Y + 9 && i < X + 4 - (y - Y - 5)) continue;
    if (y > Y + 16 && i < X + 2) continue;
    let col = pick(SAGE, 0.5 + (y - Y) * 0.02 - (i === X + 11 ? 0.3 : 0), i, y, 0.6);
    if (y >= Y + 7 && y <= Y + 11 && i >= X + 4 && i <= X + 9) col = y === Y + 7 ? '#8aa0b8' : '#34435c';          // side window
    if (y === Y + 14) col = CREAM;
    p.set(i, y, col);
  }
  p.set(X, Y + 13, '#ffe9a0'); p.set(X + 1, Y + 9, '#8aa0b8'); p.set(X + 2, Y + 8, '#8aa0b8');                     // headlamp, windscreen edge
  for (let y = Y + 2; y <= Y + 18; y++) for (let i = X + 12; i <= X + 38; i++) {                          // the box
    let col = pick(SAGE, 0.6 + (y - Y) * 0.016 - (i - X - 12) * 0.006, i, y, 0.6);
    if (y === Y + 2) col = '#86ac8a'; else if (y === Y + 14) col = CREAM;
    if (y >= Y + 5 && y <= Y + 12 && i >= X + 15 && i <= X + 35) col = pick(['#b8742c', '#e09a44', '#ffc878', '#ffe2a8'], 0.9 - (y - Y - 5) * 0.09 - Math.abs(i - X - 25) * 0.02, i, y, 0.6);
    if (y === Y + 13 && i >= X + 14 && i <= X + 36) col = '#d8a868';                                      // the counter
    p.set(i, y, col);
  }
  p.fill(X + 17, Y + 8, 6, 4, '#aab0c4'); p.fill(X + 17, Y + 8, 6, 1, '#e8ecf6'); p.set(X + 18, Y + 11, '#2a2a36'); p.set(X + 21, Y + 11, '#2a2a36');   // the espresso machine
  p.fill(X + 17, Y + 6, 5, 2, '#f6f2e6'); p.set(X + 19, Y + 6, '#c8c0b0');                                // cups on top of it
  for (const [bx, col] of [[26, '#3c6a44'], [28, '#7a2430'], [30, '#3c6a44'], [32, '#c89a40'], [34, '#7a2430']]) { p.fill(X + bx, Y + 9, 1, 3, col); p.set(X + bx, Y + 8, '#d8d0b8'); }
  p.fill(X + 25, Y + 12, 11, 1, '#8a5a30');
  for (let i = X + 11; i <= X + 40; i++) for (let y = Y - 3; y <= Y + 1; y++) {                           // the flap, striped
    if (y === Y + 1 && (i - X) % 3 === 0) continue;
    p.set(i, y, Math.floor((i - X) / 3) & 1 ? (y === Y - 3 ? '#e88aa0' : '#d16177') : (y === Y - 3 ? '#fff6e0' : CREAM));
  }
  p.fill(X + 14, Y - 13, 23, 9, '#2a1c24'); p.fill(X + 14, Y - 13, 23, 1, '#6a4a3a'); p.text('CAFFE', X + 16, Y - 11, '#ffd98c');   // the board
  p.fill(X + 17, Y - 4, 1, 1, '#2a1c24'); p.fill(X + 33, Y - 4, 1, 1, '#2a1c24');
  p.fill(X + 40, Y - 15, 1, 19, IRON.m); p.set(X + 40, Y - 16, IRON.w);                                   // the pole the bulbs hang from
  return trim(p);
}
const SCOOTER = ['........kk......', '.......kcc......', '..........c.....', '..ssssss..cc....', '.bbbbbbbb.cb....', '.bbbbbbbbbbbh...',
  'bbbbbwwbbbbbb...', '.oobbbbbb.ooo...', 'oooo.....ooooo..', 'ohho.....oohho..', '.oo.......ooo...'];
const SCOOTER_KEY = { k: '#2a2a36', c: '#9ab4c4', s: '#2a1c24', b: '#6a8ea6', w: '#ecdcb8', h: '#c8ccd8', o: '#12121a' };
/* the frame: an umbrella pine on the left, cypresses on the right (their soft edges sway, drawn per frame) */
function renderFrameTrees() {
  const p = pix(W, H), r = rng(909), NEEDLE = ['#080d1c', '#0d1626', '#152232', '#1f3138'];
  let x = 3;
  for (let y = H - 1; y >= 92; y--) {                                                                    // the pine's leaning trunk
    x += 0.055 + (y < 140 ? 0.05 : 0);
    const w = y > 200 ? 5 : y > 130 ? 4 : 3;
    for (let i = 0; i < w; i++) p.set(x + i, y, i === w - 1 ? '#3a2c34' : (hash(i, y >> 1, 3) < 0.3 ? '#1a1420' : '#120e18'));
  }
  for (const [cx, cy, rx, ry] of [[30, 84, 40, 9], [14, 90, 22, 7], [52, 88, 24, 7], [34, 76, 24, 6]])   // its flat crown, in pads
    for (let y = Math.floor(cy - ry); y <= cy + ry; y++) for (let i = Math.floor(cx - rx); i <= cx + rx; i++) {
      const v = 1 - ((i - cx) / rx) ** 2 - ((y - cy) / ry) ** 2 + (vnoise(i / 2.5, y / 2, 95) - 0.5) * 0.7;
      if (v > 0) p.set(i, y, pick(NEEDLE, 0.25 + (y - cy) / ry * 0.35 + (vnoise(i / 4, y / 3, 97) - 0.5) * 0.5, i, y));
    }
  for (const [cx, top, hw] of [[470, 82, 5], [479, 104, 4]])                                             // cypresses
    for (let y = top; y < H; y++) {
      const w = Math.min(hw, 0.8 + (y - top) * 0.12) + (vnoise(cx, y / 3, 99) - 0.5) * 1.6;
      for (let i = Math.round(cx - w); i <= Math.round(cx + w); i++) p.set(i, y, pick(NEEDLE, 0.3 - (i - cx) / (w + 1) * 0.3 + (vnoise(i / 2, y / 3, 98) - 0.5) * 0.5, i, y));
    }
  // their outlines, kept apart: needles and tips that the breeze moves a pixel this way and that
  const pine = pix(W, H), cyp = pix(W, H);
  for (let y = 0; y < H; y++) for (let i = 0; i < W; i++) {
    if (!p.alpha(i, y) || (p.alpha(i - 1, y) && p.alpha(i + 1, y) && p.alpha(i, y - 1) && p.alpha(i, y + 1))) continue;
    if (i < 90 && y < 100) pine.set(i, y, NEEDLE[1]); else if (i > 455 && y < 205) cyp.set(i, y, NEEDLE[1]);
  }
  return { base: trim(p), pine: trim(pine), cyp: trim(cyp) };
}


/* ================= The hoarding's own ad: GELATI CUPOLA, a made-up gelateria whose scoop is the dome ================= */
const AD_WINE = ['#2e121c', '#421a24', '#58242a', '#6e3230', '#86443a', '#9e5a46'];   // a deep wine-red board, warm like the roofs
const BIG = {
  C: ['.###.', '#...#', '#....', '#....', '#....', '#...#', '.###.'], U: ['#...#', '#...#', '#...#', '#...#', '#...#', '#...#', '.###.'],
  P: ['####.', '#...#', '#...#', '####.', '#....', '#....', '#....'], O: ['.###.', '#...#', '#...#', '#...#', '#...#', '#...#', '.###.'],
  L: ['#....', '#....', '#....', '#....', '#....', '#....', '#####'], A: ['.###.', '#...#', '#...#', '#####', '#...#', '#...#', '#...#'],
};
function renderAd(dim) {                               // dim: the right-hand lamp is out
  const { w, h } = BILL, p = pix(w, h);
  for (let j = 0; j < h; j++) for (let i = 0; i < w; i++) {
    let cone = 0;
    BILL_LAMPS.forEach((lx, k) => { const dx = (i - (lx - BILL.x)) / (5 + j * 0.8); cone = Math.max(cone, (k === 2 && dim ? 0.2 : 1) * Math.exp(-dx * dx) * Math.max(0, 1 - j / 15)); });
    p.set(i, j, pick(AD_WINE, 0.34 + 0.5 * cone - (dim ? 0.16 * smooth(24, 46, i) : 0), i, j, 0.5));
  }
  // the cone, and the scoop: eight ribs, a little lantern, a drip
  for (let j = 11; j <= 18; j++) { const hw = (18 - j) * 0.5 + 0.4; for (let i = Math.ceil(6 - hw); i <= Math.floor(6 + hw); i++) p.set(i, j, (i + j) & 1 ? '#e8b060' : '#b87a34'); }
  for (let j = 3; j <= 10; j++) {
    const hw = j < 10 ? 4.6 * Math.sqrt(1 - ((9 - j) / 6.6) ** 2) : 5;
    for (let i = Math.ceil(6 - hw); i <= Math.floor(6 + hw); i++) p.set(i, j, j === 10 ? '#fff0d8' : (i === 6 || (j > 4 && Math.abs(i - 6) === Math.round(hw * 0.62))) ? '#ffe6c4' : i < 6 ? '#f29a5c' : '#c8603c');
  }
  p.fill(6, 1, 1, 2, '#fff6e0'); p.set(6, 0, '#ffd870');
  // the lettering
  p.text('GELATI', 16, 2, '#ffd98c');
  let cx = 13;
  for (const ch of 'CUPOLA') {
    for (const [dx, dy, col] of [[1, 1, '#1e0a10'], [0, 0, '#fff4dc']]) BIG[ch].forEach((row, j) => { for (let i = 0; i < 5; i++) if (row[i] === '#') p.set(cx + i + dx, 10 + j + dy, col); });
    cx += 6;
  }
  for (let i = 16; i < 39; i += 2) p.set(i, 8, '#ffd98c');
  return p.done();
}

/* ================= People: one function draws them all (after rome.js), lit by the lamps or by the sky ================= */
const SKINS = ['#f2c8a0', '#e2ac82', '#c48c60', '#8c5a3c'], HAIRS = ['#1c1412', '#3c2418', '#6c4222', '#d0a858', '#96421c', '#d4d0cc'];
const TOPS = ['#d8404a', '#e8c040', '#ece6da', '#3a6ac8', '#2a9a8a', '#e87aa8', '#e8823a', '#4a9a4a', '#8a5ac8', '#c8d0e0', '#b8323a', '#58b0d8'];
const BOTS = ['#33466e', '#22202c', '#b09a70', '#d8d4cc', '#3a3a58', '#4a3a30'];
const NIGHT = '#12152e', WARM_RIM = '#ffd9a0', COOL_RIM = '#8e9cd6';
/** a look: proportions for a height of h px, colours dimmed for the dusk (dk) with a rim of lamplight (warm 0..1) or sky */
function look(h, skin, hair, top, bot, o = {}) {
  const dk = o.dk ?? 0.34, rim = mixHex(COOL_RIM, WARM_RIM, o.warm ?? 0), m = c => mixHex(c, NIGHT, dk);
  const hh = h >= 22 ? 5 : h >= 14 ? 4 : 3, th = Math.round(h * 0.36);
  return { h, hh, hw: h >= 19 ? 4 : 3, th, tw: h >= 22 ? 7 : h >= 19 ? 6 : h >= 14 ? 5 : 4, ll: h - hh - th,
    skin: m(SKINS[skin]), hair: m(HAIRS[hair]), hairL: mixHex(m(HAIRS[hair]), rim, 0.45), top: m(top), topD: mixHex(top, NIGHT, Math.min(0.9, dk + 0.28)),
    topL: mixHex(m(top), rim, 0.5), bot: m(bot), botL: mixHex(m(bot), rim, 0.45), skinL: mixHex(m(SKINS[skin]), rim, 0.35),
    shoe: m('#221a20'), long: o.long || 0, skirt: o.skirt ? m(o.skirt) : null,
    hat: o.hat ? m(o.hat) : null, bag: o.bag ? m(o.bag) : null, apron: o.apron ? m(o.apron) : null };
}
/** the look of someone standing at (x, y): the nearer a lamp, the less dusk and the warmer the rim */
const lookAt = (x, y, h, skin, hair, top, bot, o = {}) => { const L = lampLight(x, y); return look(h, skin, hair, top, bot, { dk: 0.5 - L * 0.3, warm: Math.min(1, L * 1.6), ...o }); };
/**
 * One person, feet on row fy, centred on column x. Returns the row of the top of the head.
 * o.view: 0 from behind (watching the city), 1 facing us, 2 in profile (o.dir: 1 right, -1 left)
 * o.step: stride frame (profile); o.sit: seated on an edge at fy (from behind, facing us with the legs hanging, or in profile);
 * o.knees: seated in profile on a step, knees drawn up, an arm resting on them; o.rim: -1 lit from the left (a lamp);
 * o.lean: head px towards a neighbour; o.aL / o.aR: 0 down, 1 straight up, 2 bent up with a phone, 3 out, 4 bent up,
 * 5 hand to the face, 6 hand to the mouth (profile)
 */
function person(x, fy, P, o = {}) {
  x = Math.round(x); fy = Math.round(fy);
  const { hw, hh, tw, th } = P, view = o.view || 0, dir = o.dir || 1, side = view === 2;
  const legs = o.sit ? 0 : P.ll;
  const ty = fy - legs - th + 1, hy = ty - hh, bw = side ? tw - 2 : tw, tx = x - (bw >> 1), legC = P.skirt ? P.skin : P.bot;
  if (legs) {
    const ly = fy - legs + 1;
    if (side) {
      if (o.step) {
        R(x - 2, ly, 4, 2, legC);
        R(dir > 0 ? x + 1 : x - 2, ly + 2, 2, legs - 2, legC); R(dir > 0 ? x + 1 : x - 3, fy, 3, 1, P.shoe);
        R(dir > 0 ? x - 2 : x + 1, ly + 2, 2, legs - 3, legC); R(dir > 0 ? x - 2 : x + 1, fy - 1, 2, 1, P.shoe);
      } else { R(x - 1, ly, 2, legs, legC); R(dir > 0 ? x - 1 : x - 2, fy, 3, 1, P.shoe); }
      if (P.skirt) R(x - 2, ly, 4, (legs >> 1) + 1, P.skirt);
    } else {
      const lw = bw >= 7 ? 3 : 2;
      R(tx, ly, bw, 2, P.bot); R(tx, ly + 2, lw, legs - 2, legC); R(tx + bw - lw, ly + 2, lw, legs - 2, legC);
      R(tx, fy, lw, 1, P.shoe); R(tx + bw - lw, fy, lw, 1, P.shoe);
      if (P.skirt) R(tx, ly, bw, (legs >> 1) + 1, P.skirt);
    }
  } else if (side && o.knees) {                       // seated on the step in profile, knees up, feet on the same step
    const kx = x + dir * 5, ky = fy - (P.ll >> 1);
    ctx.globalAlpha = 0.35; R(Math.min(x - 2, x + dir * 7), fy + 1, 10, 1, NIGHT); ctx.globalAlpha = 1;   // their shadow on the step
    for (let k = 0; k <= 4; k++) R(x + dir * (1 + k), Math.round(lerp(fy - 1, ky, k / 4)), 1, 2, P.bot);   // the thigh rises to the knee
    R(dir > 0 ? kx : kx - 1, ky, 2, fy - ky, legC); R(dir > 0 ? kx : kx - 2, fy, 3, 1, P.shoe);           // the shin, the shoe
    R(kx, ky, 1, 1, P.botL);
  } else if (side) {                                   // seated in profile: thighs forward, shins down
    R(dir > 0 ? x - 1 : x - 3, fy - 1, 5, 2, P.bot); R(dir > 0 ? x + 2 : x - 3, fy + 1, 2, P.ll - 2, legC); R(dir > 0 ? x + 2 : x - 4, fy + P.ll - 1, 3, 1, P.shoe);
  } else if (view === 1) {                             // seated facing us: the legs hang down in front
    R(tx, fy - 1, bw, 2, P.bot); R(tx, fy + 1, 2, 5, legC); R(tx + bw - 2, fy + 1, 2, 5, legC); R(tx, fy + 6, 2, 1, P.shoe); R(tx + bw - 2, fy + 6, 2, 1, P.shoe);
  } else R(tx - 1, fy - 1, bw + 2, 2, P.bot);          // seated, from behind
  const cut = o.knees ? 1 : 0, sx = dir > 0 ? tx + cut : tx;   // hunched over the knees: the back's top corner rounds off
  R(sx, ty, bw - cut, 1, P.top); R(tx, ty + 1, bw, th - 1, P.top);
  if (!side) R(tx + bw - 1, ty + 1, 1, th - 1, P.topD);
  if (view !== 1) R(sx, ty, bw - cut, 1, P.topL);      // the light on the shoulders
  if (o.rim) R(o.rim < 0 ? tx : tx + bw - 1, ty + 1, 1, th - 1, P.topL);   // and down the side that faces the lamp
  if (P.bag) { if (view === 0) R(tx + 1, ty + 1, bw - 2, th - 2, P.bag); else if (side) R(dir > 0 ? tx - 1 : tx + bw - 1, ty + 1, 2, th - 2, P.bag); }
  if (P.apron && view !== 0) R(side ? (dir > 0 ? tx + 1 : tx) : tx + 1, ty + 2, side ? bw - 1 : bw - 2, th - 2 + (legs >> 1), P.apron);
  if (side) {
    const st = o.aR || 0;
    if (st === 0 && o.knees) { R(x + dir, ty + 1, 1, 2, P.topD); R(dir > 0 ? x + 2 : x - 3, ty + 3, 2, 1, P.topD); R(x + dir * 4, ty + 3, 1, 1, P.skin); }
    else if (st === 0) { const ax = x + (o.step ? -dir : 0); R(ax, ty + 1, 1, th - 2, P.topD); R(ax, ty + th - 1, 1, 1, P.skin); }
    else if (st === 1) { R(x + dir, hy - 1, 1, ty - hy + 2, P.topD); R(x + dir, hy - 3, 1, 2, P.skin); }
    else if (st === 2) { for (let k = 0; k < 4; k++) R(x + dir * (1 + k), ty + 1 - k, 1, 1, k < 3 ? P.topD : P.skin); R(x + dir * 5 - (dir < 0 ? 1 : 0), ty - 5, 2, 3, '#0e1018'); }
    else if (st === 3) { R(dir > 0 ? x + 1 : x - 3, ty + 2, 3, 1, P.topD); R(x + dir * 4, ty + 2, 1, 1, P.skin); }
    else { R(x + dir, ty + 1, 1, 2, P.topD); R(x + dir * 2, ty, 1, 1, P.skin); }
  } else {
    const arm = (ax, st, sg) => {
      if (!st) { R(ax, ty + 1, 1, th - 2, P.topD); R(ax, ty + th - 1, 1, 1, P.skin); }
      else if (st === 1) { R(ax, hy - 1, 1, ty - hy + 2, P.topD); R(ax, hy - 3, 1, 2, P.skin); }
      else if (st === 2 || st === 4) {
        R(ax, hy + 2, 1, ty - hy, P.topD); R(ax, hy + 1, 1, 1, P.skin);
        if (st === 2) { const p0 = sg < 0 ? ax - 1 : ax; R(p0, hy - 2, 2, 3, '#0e1018'); if (view === 0) { R(p0, hy - 2, 2, 1, '#ffe2a8'); R(p0, hy - 1, 2, 1, '#8ab0e8'); } }
      } else if (st === 3) { R(sg < 0 ? ax - 2 : ax, ty + 1, 3, 1, P.topD); R(sg < 0 ? ax - 3 : ax + 3, ty + 1, 1, 1, P.skin); }
      else { R(ax, ty, 1, 2, P.topD); R(ax + (sg < 0 ? 1 : -1), hy + hh - 2, 1, 2, P.skin); }
    };
    arm(tx - 1, o.aL, -1); arm(tx + bw, o.aR, 1);
  }
  const hx = x - (hw >> 1) + (o.lean || 0), hr = hh >= 5 ? 2 : 1;
  if (view === 0) {
    R(hx, hy, hw, hh, P.hair); R(hx, hy, hw, 1, P.hairL);
    if (P.long) R(hx, hy + hh, hw, P.long, P.hair); else R(hx + 1, hy + hh - 1, hw - 2, 1, P.skin);
  } else if (view === 1) {
    R(hx, hy, hw, hh, P.skin); R(hx, hy, hw, hr, P.hair);
    if (P.long) { R(hx - 1, hy + 1, 1, hh + P.long - 1, P.hair); R(hx + hw, hy + 1, 1, hh + P.long - 1, P.hair); }
  } else { R(hx, hy, hw, hh, P.skin); R(hx, hy, hw, hr, P.hair); R(dir > 0 ? hx : hx + hw - 1, hy, 1, hh - 1 + P.long, P.hair); }
  if (o.rim && side) { const ex = o.rim < 0 ? hx : hx + hw - 1; R(ex, hy + hr, 1, hh - hr - 1, ex === (dir > 0 ? hx : hx + hw - 1) ? P.hairL : P.skinL); }
  if (P.hat) { R(hx, hy - 1, hw, 2, P.hat); if (side) R(dir > 0 ? hx + hw : hx - 1, hy, 1, 1, P.hat); else R(hx - 1, hy, hw + 2, 1, P.hat); }
  return hy;
}

/* ----- the cast: who is where, what they do, and their crowd rank (0 stays while anybody does; pairs share one) ----- */
const TY = FLOOR + 4;                                  // feet of the people standing on the terrace
const CAST = [
  { x: 201, y: TY, rank: 0, act: 'busker', P: lookAt(201, TY, 20, 1, 1, '#b8623a', BOTS[5], { hat: '#3a2c2c' }) },
  { x: 133, y: TY - 1, rank: 0, act: 'vendor', P: lookAt(133, TY, 20, 0, 0, '#3a4a5a', BOTS[1], { apron: '#ece6da', dk: 0.16, warm: 1 }) },
  { x: 144, y: TY, rank: 0.58, act: 'customer', P: lookAt(144, TY, 20, 2, 0, TOPS[3], BOTS[2]) },
  { x: 224, y: RAIL, rank: 0.12, act: 'railA', mate: 1, P: lookAt(224, TY, 20, 1, 0, TOPS[9], BOTS[0]) },
  { x: 233, y: RAIL, rank: 0.12, act: 'railB', mate: -1, P: lookAt(233, TY, 19, 0, 4, TOPS[0], BOTS[3], { long: 3, skirt: '#b8323a' }) },
  { x: 262, y: TY, rank: 0.42, act: 'film', ph: 0.1, P: lookAt(262, TY, 20, 0, 2, TOPS[1], BOTS[4], { long: 2 }) },
  { x: 286, y: TY, rank: 0.64, act: 'parent', P: lookAt(286, TY, 20, 2, 0, TOPS[7], BOTS[1], { bag: '#6a4a3a' }) },
  { x: 294, y: TY, rank: 0.64, act: 'child', P: lookAt(294, TY, 12, 2, 0, TOPS[11], BOTS[0]) },
  { x: 60, y: TY, rank: 0.9, act: 'photo', P: lookAt(60, TY, 20, 0, 1, '#4a4a58', BOTS[1], { hat: '#2a2a34' }) },
  { x: 430, y: TY, rank: 0.3, act: 'sketch', P: lookAt(430, TY, 20, 0, 5, TOPS[4], BOTS[2], { hat: '#d8c8a0' }) },
  { x: 201, y: STEPS[1], rank: 0.22, act: 'bottleA', P: lookAt(201, 234, 24, 1, 1, TOPS[6], BOTS[0]) },
  { x: 221, y: STEPS[1], rank: 0.22, act: 'bottleB', P: lookAt(221, 234, 24, 0, 3, TOPS[2], BOTS[1], { long: 3 }) },
  { x: 296, y: STEPS[0], rank: 0.85, act: 'chat', lean: 1, ph: 0.2, P: lookAt(296, 224, 22, 3, 0, TOPS[8], BOTS[1]) },
  { x: 307, y: STEPS[0], rank: 0.85, act: 'chat', lean: -1, ph: 0.7, P: lookAt(307, 224, 22, 0, 2, TOPS[5], BOTS[3], { long: 2 }) },
  { x: 148, y: STEPS[2], rank: 0.46, act: 'alone', ph: 0.3, P: lookAt(148, 246, 27, 2, 0, TOPS[10], BOTS[0], { bag: '#3a4a6a' }) },
  { x: 350, y: STEPS[2], rank: 0.7, act: 'still', P: lookAt(350, 246, 27, 1, 0, TOPS[3], BOTS[1]) },
  { x: 362, y: STEPS[2], rank: 0.7, act: 'rest', lean: -2, P: lookAt(362, 246, 26, 0, 4, TOPS[1], BOTS[3], { long: 4 }) },
  { x: 408, y: STEPS[3], rank: 0.78, act: 'alone', ph: 0.8, P: lookAt(408, 260, 30, 3, 0, TOPS[2], BOTS[4], { hat: '#c8b88a' }) },
  { x: 96, y: STEPS[3], rank: 0.95, act: 'still', P: lookAt(96, 260, 30, 0, 1, TOPS[4], BOTS[1], { long: 3 }) },
];
const WALKER_A = { t0: 4, t1: 64, dir: 1, rank: 0.36, P: look(21, 1, 2, TOPS[9], BOTS[0], { dk: 0.34, warm: 0.5, bag: '#8a5a3a' }) };
const WALKER_B = { t0: 150, t1: 214, dir: -1, rank: 0.52, P: look(21, 0, 5, '#7a8a9a', BOTS[4], { dk: 0.34, warm: 0.5 }) };   // with a small dog
const walkF = t => Math.floor(t * 3.2) & 1;            // stride frame (3.2 a second: a whole number per loop)
let DL = [];                                           // the frame's people, drawn back to front
const put = (y, f) => DL.push({ y, f });
function castPose(c, t) {
  const o = { view: 0 }, bells = t >= BELLS[0] - 0.5 && t < BELLS[4] + 3;
  switch (c.act) {
    case 'busker': o.view = 1; o.lean = Math.round(0.6 * wave(t, 120)); o.aR = 0; o.aL = 0; break;
    case 'vendor': {
      const s = ((t % SERVE_EVERY) + SERVE_EVERY) % SERVE_EVERY;
      o.view = 2; o.dir = s < 5 ? -1 : 1; o.aR = s < 5 || (s > 6 && s < 9.5) ? 3 : 0;
      break;
    }
    case 'customer': o.view = 2; o.dir = -1; if (frac((t - 8) / SERVE_EVERY) < 0.5 && frac(t / 5) < 0.3) o.aR = 6; break;
    case 'railA': case 'railB': {
      const u = frac(t / 40 + 0.3);
      o.view = 1; o.sit = true;
      if (u > 0.5 && u < 0.58) { o.view = 2; o.dir = c.mate; o.lean = c.mate; }                 // a kiss
      else if (u > 0.2 && u < 0.5) o.lean = c.mate;
      else if (c.mate < 0 && u > 0.8 && u < 0.88) o.aL = 1;                                    // she points at the dome
      break;
    }
    case 'film': if (bells || frac(t / 24 + c.ph) < 0.62) o.aR = 2; break;
    case 'parent': { const u = frac(t / 20); if (bells || u < 0.14) o.aL = 1; else if (u > 0.5 && u < 0.6) { o.view = 2; o.dir = 1; } break; }
    case 'child': if (frac(t / 20) < 0.14 || bells) o.aL = 1; break;
    case 'photo': o.lean = frac(t / 16) < 0.4 ? 1 : 0; break;
    case 'sketch': o.view = 2; o.dir = -1; o.sit = true; o.aR = hash(7, step(t, 192), 3) < 0.55 ? 3 : 4; break;
    case 'bottleA': case 'bottleB': {                   // turned to each other, knees up, the lamp on their left
      const a = c.act === 'bottleA', s = (((t - (a ? 12 : 27)) % SERVE_EVERY) + SERVE_EVERY) % SERVE_EVERY;
      o.sit = true; o.view = 2; o.dir = a ? 1 : -1; o.knees = true; o.rim = -1;
      if (inWin(t, TOAST)) o.aR = 1;
      else if (s < 3.2) o.aR = 4;
      else o.lean = frac(t / 40) < 0.3 ? 2 * o.dir : o.dir;   // heads forward over their knees, now and then close together
      break;
    }
    case 'chat': o.sit = true; if (frac(t / 30 + c.ph) < 0.45) o.lean = c.lean; if (hash(c.x, step(t + c.ph * 3, 80), 31) < 0.25) o[c.lean > 0 ? 'aR' : 'aL'] = 3; break;
    case 'alone': o.sit = true; if (frac(t / 48 + c.ph) < 0.3) o.aR = 2; else if (frac(t / 30 + c.ph) < 0.2) o.lean = 1; break;
    case 'rest': o.sit = true; o.lean = frac(t / 60) < 0.8 ? c.lean : 0; break;
    default: o.sit = true; if (frac(t / 40 + c.x * 0.01) < 0.15) o.lean = -1;
  }
  return o;
}
const NOTE = [['.1', '.1', '11'], ['.11', '.1.', '11.']];
// a straw-covered fiasco: upright, and tipped to a mouth on the left (flipped: on the right)
const FIASCO_ROWS = [['.c.', '.n.', 'sSd', 'sSd', '.s.'], ['..sS', 'nsSd', '..sd']];
const FIASCO_KEY = { c: '#c8bca0', n: '#2a4430', S: '#d8b070', s: '#a8844a', d: '#6a4e30' };
const FIASCO_X = 211;                                  // where it stands on the step, between their feet
let FIASCO;
function drawCast(t, crowd) {
  DL = [];
  for (const c of CAST) {
    if (!here(c.rank, crowd)) continue;
    const o = castPose(c, t), P = c.P, x = c.x, y = c.act === 'sketch' ? TY - 8 : c.y;
    put(c.y, () => {
      const hy = person(x, y, P, o), ty = hy + P.hh;
      switch (c.act) {
        case 'busker': {                                                                  // the guitar, the strumming hand, the open case, a note now and then
          const s = step(t, 768) & 1;
          R(x - 1, ty + 3, 5, 3, '#a8662c'); R(x, ty + 2, 3, 1, '#a8662c'); R(x - 1, ty + 3, 5, 1, '#d08a44'); R(x + 1, ty + 4, 1, 1, '#2a1810');
          for (let k = 1; k <= 6; k++) R(x - 1 - k, ty + 3 - Math.round(k * 0.6), 1, 1, k === 6 ? '#d8c8a0' : '#5a3a22');
          R(x + 2, ty + 3 + s, 1, 1, P.skin); R(x - 5, ty + 1, 1, 1, P.skin);
          R(x - 14, TY + 1, 10, 2, '#1a1216'); R(x - 13, TY + 1, 8, 1, '#7a2a3a'); if (hash(3, step(t, 240), 5) < 0.5) R(x - 10, TY + 1, 1, 1, '#ffe9a0');
          for (let k = 0; k < 2; k++) {
            const { cyc, u } = cycle(t, 40, k * 0.5);
            if (inWin(t, [TOAST[0] - 4, TOAST[1] + 6]) || u > 0.8) continue;                // the song ends: a pause before the next one
            ctx.globalAlpha = Math.min(1, u * 6) * (1 - u / 0.8) * 0.9; ctx.fillStyle = '#ffe9a0';
            const nx = x + 8 + u * 12 + Math.round(Math.sin(u * 9 + k) * 2), ny = hy - 2 - u * 18;
            NOTE[(cyc + k) & 1].forEach((row, j) => { for (let i = 0; i < row.length; i++) if (row[i] === '1') ctx.fillRect(Math.round(nx) + i, Math.round(ny) + j, 1, 1); });
            ctx.globalAlpha = 1;
          }
          break;
        }
        case 'vendor': if (o.aR === 3 && o.dir > 0) R(x + 5, ty + 1, 1, 2, '#f6f2e6'); break;
        case 'customer': R(o.aR === 6 ? x - 2 : x - 1, o.aR === 6 ? ty - 1 : ty + P.th - 1, 1, 2, '#f6f2e6'); break;
        case 'film': case 'alone': if (o.aR === 2) halo(HL.phone, x + (P.tw >> 1) + 1, hy - 1, 0.7); break;
        case 'photo': {                                                                   // his camera on its tripod
          const cx = x + 8;
          for (let k = 0; k <= 12; k++) { R(cx - Math.round(k * 0.3), TY - 12 + k, 1, 1, IRON.d); R(cx + Math.round(k * 0.3), TY - 12 + k, 1, 1, IRON.d); }
          R(cx, TY - 12, 1, 9, IRON.m); R(cx - 2, TY - 15, 5, 3, '#1a1c28'); R(cx - 2, TY - 15, 5, 1, '#4a4e60'); R(cx + 3, TY - 14, 1, 1, '#8aa0c8');
          ctx.globalAlpha = 0.5 + 0.5 * wave(t, 80); R(cx - 2, TY - 14, 1, 1, '#ff4a3a'); ctx.globalAlpha = 1;
          break;
        }
        case 'sketch': {                                                                  // a folding stool, an easel, the dome half drawn
          R(x - 2, TY - 7, 5, 1, '#5a4030'); R(x - 2, TY - 6, 1, 7, '#3a2a20'); R(x + 2, TY - 6, 1, 7, '#3a2a20');
          const ex = x - 11;
          for (let k = 0; k <= 16; k++) { R(ex - Math.round(k * 0.2), TY - 16 + k, 1, 1, '#4a3424'); R(ex + 4 + Math.round(k * 0.12), TY - 16 + k, 1, 1, '#3a2a20'); }
          R(ex - 2, TY - 19, 8, 9, '#e8dcc0'); R(ex - 2, TY - 19, 8, 1, '#fff6e0'); R(ex + 5, TY - 18, 1, 8, '#b8a888');
          R(ex, TY - 14, 4, 1, '#a89a8a'); R(ex + 1, TY - 16, 2, 2, '#c65e3e'); R(ex + 1, TY - 17, 1, 1, '#8a7a70');
          if (o.aR === 3) R(x - 5, ty + 2, 1, 1, '#2a2430');                               // the pencil
          break;
        }
        case 'bottleA': case 'bottleB': {                                                 // their fiasco of Chianti
          const d = o.dir;
          if (o.aR === 4) ctx.drawImage(FIASCO[d > 0 ? 1 : 2], d > 0 ? x + 2 : x - 6, hy + 2);   // tipped to the mouth
          else if (o.aR === 1 && c.act === 'bottleA') ctx.drawImage(FIASCO[0], x, hy - 8);       // raised to the busker
          else if (c.act === 'bottleA' && castPose(CAST.find(q => q.act === 'bottleB'), t).aR !== 4)
            ctx.drawImage(FIASCO[0], FIASCO_X - 1, c.y - 4);                                     // standing on the step between them
          break;
        }
      }
    });
  }
  for (const wk of [WALKER_A, WALKER_B]) {
    if (!here(wk.rank, crowd) || t < wk.t0 || t >= wk.t1) continue;
    const u = (t - wk.t0) / (wk.t1 - wk.t0), x = wk.dir > 0 ? -12 + u * (W + 24) : W + 12 - u * (W + 24), y = TY + 3, d = wk.dir, f = walkF(t);
    put(y, () => {
      person(x, y, wk.P, { view: 2, dir: d, step: f });
      if (wk !== WALKER_B) return;
      const dx = Math.round(x) + d * 10;                                                    // her small dog trots ahead on its lead
      R(dx - 2, y - 3, 5, 2, '#c8b08a'); R(d > 0 ? dx + 2 : dx - 3, y - 5, 2, 2, '#c8b08a'); R(d > 0 ? dx - 3 : dx + 3, y - 4, 1, 1, '#8a7458');
      R(dx - 2 + f, y - 1, 1, 2, '#8a7458'); R(dx + 2 - f, y - 1, 1, 2, '#8a7458');
      for (let k = 1; k < 4; k++) R(Math.round(x) + d * (2 + k * 2), y - 7 + k, 1, 1, '#3a3038');
    });
  }
  DL.sort((a, b) => a.y - b.y);
  for (const q of DL) q.f();
}

/* ================= The cart's life, the bulbs, the lamps' light, moths and bats ================= */
const BULBS = [];                                      // the string from the cart's pole to the left lamp
{ const x0 = CART.x + 40, y0 = CART.y - 15, x1 = LAMPS[0], y1 = LAMP_Y + 12; for (let k = 1; k < 9; k++) { const u = k / 9; BULBS.push([lerp(x0, x1, u), lerp(y0, y1, u) + Math.sin(u * Math.PI) * 6, k]); } }
const BULB_COL = ['#ffe9a0', '#ffc468', '#ffd0a0', '#fff2c8'];
function drawCartLife(t, crowd) {
  halo(HL.cart, CART.x + 25, CART.y + 9, 0.9 + 0.1 * wave(t, 11));
  blit(CART_C);
  const sw = wave(t, 16, 0.2) * 0.8 + wave(t, 43, 0.5) * 0.4;                              // the string stirs in the breeze
  let px0 = CART.x + 40, py0 = CART.y - 15;
  for (const [bx, by, k] of BULBS) {
    const x = Math.round(bx + sw * Math.sin(k / 9 * Math.PI)), y = Math.round(by);
    R(Math.min(px0, x), Math.round((py0 + y) / 2), Math.abs(x - px0) || 1, 1, '#1a1e2a');
    R(x, y, 1, 2, BULB_COL[k & 3]);
    halo(HL.bulb, x, y + 1, 0.75 + 0.25 * wave(t, 400 + k * 37, k * 0.3));
    px0 = x; py0 = y;
  }
  // a wisp of steam off the machine; a proper puff when the vendor pulls a coffee
  const s = ((t % SERVE_EVERY) + SERVE_EVERY) % SERVE_EVERY, pull = crowd > 0 && s > 1 && s < 5;
  for (let k = 0; k < (pull ? 6 : 2); k++) {
    const { u } = cycle(t, 80, k / 6), x = CART.x + 20 + wave(t, 30 + k * 7, k * 0.2) * 2 * u - u * 3, y = CART.y + 7 - u * 12;
    ctx.globalAlpha = (pull ? 0.6 : 0.3) * Math.sin(Math.PI * u); R(Math.round(x), Math.round(y), pull && u > 0.3 ? 2 : 1, 1, '#f4ecf0');
  }
  ctx.globalAlpha = 1;
}
const MOTHS = Array.from({ length: 6 }, (_, i) => ({ l: i % 2 ? 3 : 0, k: 37 + i * 13, k2: 53 + i * 17, ph: i * 0.37, r: 4 + (i % 3) * 2.5 }));
const BATS = Array.from({ length: 4 }, (_, i) => ({ x: LAMPS[i & 1] + (i < 2 ? -14 : 18), k1: 9 + i * 4, k2: 23 + i * 6, k3: 13 + i * 5, k4: 31 + i * 4, ph: i * 0.29 }));
function drawLampLife(t) {
  const fl = flick(t, LAMP_FLICK, 21);
  LANTERNS.forEach(([x, y], i) => {
    const a = (i === 1 ? fl : 1) * (0.94 + 0.06 * wave(t, 1100 + i * 90, i * 0.3));
    if (a < 0.9) R(x - 1, y - 1, 3, 4, `rgba(60,36,20,${(0.9 - a).toFixed(3)})`);
    halo(HL.lampCore, x, y, a);
    if (i % 3 === 0) halo(HL.lamp, x, y + 6, 0.9 * (i === 0 ? (2 + fl) / 3 : 1));
  });
  for (const m of MOTHS) {                                                                // moths round the top lanterns
    const [lx, ly] = LANTERNS[m.l], a = TAU * (m.k * t / LOOP + m.ph);
    dot(lx + Math.cos(a) * m.r, ly + Math.sin(TAU * (m.k2 * t / LOOP + m.ph)) * m.r * 0.6, '#fff2d8');
  }
  for (const b of BATS) {                                                                 // bats hunting them: quick, jinking, dark
    const x = b.x + 20 * Math.sin(TAU * (b.k1 * t / LOOP + b.ph)) + 7 * Math.sin(TAU * (b.k2 * t / LOOP + b.ph * 2));
    const y = LAMP_Y - 16 + 13 * Math.sin(TAU * (b.k3 * t / LOOP + b.ph * 3)) + 5 * Math.sin(TAU * (b.k4 * t / LOOP));
    const X = Math.round(x), Y = Math.round(y), up = Math.floor(t * 9 + b.ph * 7) & 1;
    R(X, Y, 1, 1, '#16121e'); R(X - 1, Y + (up ? -1 : 0), 1, 1, '#16121e'); R(X + 1, Y + (up ? -1 : 0), 1, 1, '#16121e');
  }
}

/* ================= The rose hedges nod in the breeze; pigeons pick about on the steps; the frame's trees stir ================= */
function drawHedges(t) {
  for (const b of BLOOMS) R(b.x + Math.round(wave(t, 18, b.ph) * 0.9 + wave(t, 47, b.ph * 3) * 0.5), b.y, b.two ? 2 : 1, 1, b.col);
}
const PIGEON = {   // 7x5, facing right: G body, D wing, H head, N neck, B beak, L legs
  up: ['.....HH', '....HHB', '.GDDGN.', 'GGDDGG.', '..L.L..'],
  peck: ['.......', '.GDDG..', 'GGDDGNH', 'GGGGGHB', '..L.L..'],
  walk: ['.....HH', '....HHB', '.GDDGN.', 'GGDDGG.', '.L...L.'],
};
const PIG_COL = { G: '#6c6c84', D: '#4c4c64', H: '#7c7c96', N: '#587e70', B: '#b0947a', L: '#a05048' };
const STEP_PIGEONS = [[246, 38, 229, 3, 0.1], [410, 40, 240, 2, 0.45], [270, 56, 254, 2, 0.7], [220, 70, 267, 3, 0.25], [78, 30, 255, 2, 0.6], [96, 40, 231, 2, 0.9]];   // cx, reach, feet row, trips a loop, phase
let PIG;
function drawStepPigeons(t) {
  STEP_PIGEONS.forEach(([cx, ax, fy, k, ph], i) => {
    const x = cx + ax * wave(t, k, ph), vel = Math.cos(TAU * (k * t / LOOP + ph)), moving = Math.abs(vel) > 0.35;
    const pose = moving ? (step(t, 1440) & 1 ? 'walk' : 'up') : hash(i, step(t, 480), 3) < 0.55 ? 'peck' : 'up';
    ctx.drawImage(PIG[pose][vel < 0 ? 1 : 0], Math.round(x) - 3, fy - 4);
  });
}
function drawTrees(t) {
  blit(TREES_C.base);
  ctx.drawImage(TREES_C.pine.c, TREES_C.pine.x + Math.round(wave(t, 14, 0.1) * 1.1 + wave(t, 37, 0.4) * 0.5), TREES_C.pine.y);
  ctx.drawImage(TREES_C.cyp.c, TREES_C.cyp.x + Math.round(wave(t, 11, 0.6) * 1.1 + wave(t, 41, 0.2) * 0.5), TREES_C.cyp.y);
}

/* ================= The cat on the balustrade ================= */
const CAT_SIT = ['k..k...', 'kkkk...', 'kwkk...', '.kkkk..', '.kkkkk.', '.kkkkk.', 'kkkkkk.'];
const CAT_WALK_F = [['.......kk', 'k.....kkk', '.kkkkkkk.', '.kkkkkk..', '.k.k.k.k.'], ['.......kk', '.k....kkk', '.kkkkkkk.', '.kkkkkk..', '..kk.kk..']];
const CAT_KEY = { k: '#15121c', w: '#3a3040' };
let CAT_C;
function drawCat(t) {
  const A = 312, B = 352;
  for (const [a, b, d] of CAT_WALK) if (t >= a && t < b) {
    const u = (t - a) / (b - a), x = Math.round(d > 0 ? lerp(A, B, u) : lerp(B, A, u));
    ctx.drawImage(CAT_C.walk[d > 0 ? 0 : 1][Math.floor(t * 4) & 1], x - 4, RAIL - 5);
    return;
  }
  const x = t >= CAT_WALK[0][1] && t < CAT_WALK[1][0] ? B : A, look = hash(9, step(t, 60), 3) < 0.3;   // now and then it looks round at us
  ctx.drawImage(CAT_C.sit, x - 3, RAIL - 7);
  const ts = wave(t, 30, 0.2) * 1.2 + wave(t, 97, 0.5) * 0.5;                                // the tail hangs down the near side and swings
  for (let j = 0; j < 5; j++) R(x + 3 + Math.round(ts * j / 4), RAIL + j, 1, 1, '#15121c');
  if (look && frac(t / 4) < 0.92) { R(x - 3, RAIL - 5, 1, 1, '#e6f08a'); R(x - 1, RAIL - 5, 1, 1, '#e6f08a'); }
}

/* ================= Swifts over the roofs, the dome's pigeons, fireflies in the trees ================= */
const SWIFT = [['1...1', '.1.1.', '..1..'], ['.....', '11.11', '..1..'], ['..1..', '.1.1.', '1...1']];
const SWIFTS = [];
{
  const r = rng(321);
  for (let i = 0; i < 13; i++) SWIFTS.push({ cx: 60 + r() * 360, cy: 46 + r() * 44, ax: 40 + r() * 70, ay: 10 + r() * 22,
    ka: 5 + Math.floor(r() * 5), kb: 7 + Math.floor(r() * 7), pa: r(), pb: r(), id: i, low: false });
  for (let i = 13; i < 22; i++) SWIFTS.push({ cx: 70 + r() * 350, cy: 118 + r() * 40, ax: 50 + r() * 80, ay: 10 + r() * 16,   // hunting low over the roofs and the river
    ka: 5 + Math.floor(r() * 4), kb: 7 + Math.floor(r() * 6), pa: r(), pb: r(), id: i, low: true });
}
function drawSwift(x, y, f) {
  const X = Math.round(x), Y = Math.round(y);
  ctx.fillStyle = '#0e1226';
  SWIFT[f].forEach((row, j) => { for (let i = 0; i < 5; i++) if (row[i] === '1') ctx.fillRect(X - 2 + i, Y - 1 + j, 1, 1); });
}
function drawSwifts(t, low, board) {
  for (const s of SWIFTS) {
    if (s.low !== low) continue;
    const x = s.cx + s.ax * Math.sin(TAU * (s.ka * t / LOOP + s.pa)), y = s.cy + s.ay * Math.sin(TAU * (s.kb * t / LOOP + s.pb));
    if (low && board && x > BILL.x - 6 && x < BILL.x + BILL.w + 6 && y > BILL.y - 8 && y < BILL.y + BILL.h + 4) continue;   // (they pass behind the hoarding)
    drawSwift(x, y, hash(s.id, step(t, 480), 9) < 0.4 ? 1 : Math.floor(t * 8 + s.id) % 3);     // they glide between bursts of flapping
  }
  PARTY.forEach(([a, b], pi) => {                                                           // a screaming party, low and fast, all together
    if (low || t < a || t >= b) return;
    for (let i = 0; i < 7; i++) {
      const u = (t - a) / (b - a) - i * 0.014 - (i % 3) * 0.006;
      if (u < 0 || u > 1) continue;
      const x = pi ? lerp(W + 16, -16, u) : lerp(-16, W + 16, u), y = 84 + 20 * Math.sin(u * TAU * 1.5 + pi) + (i % 3) * 4 - (i >> 1) * 2;
      drawSwift(x, y, Math.floor(t * 9 + i) % 3);
    }
  });
}
const PERCHES = [[266, 46], [269, 46], [273, 46], [252, 83], [260, 83], [279, 83], [288, 83], [183, 52], [187, 52], [190, 52], [214, 92], [226, 92], [248, 98], [291, 98]];
const FLOCK = PERCHES.map(([x, y], i) => {
  const r = rng(900 + i);
  return { x, y, i, laps: 1 + (r() < 0.45 ? 1 : 0), phi: r() * TAU, rx: 20 + r() * 34, ry: 9 + r() * 14, bx: (r() < 0.5 ? -1 : 1) * (20 + r() * 50), by: 10 + r() * 26, del: r() * 0.06 };
});
const FLAP = [['1...1', '.121.', '.....'], ['.....', '11211', '.....'], ['.....', '.121.', '1...1']];
function drawFlock(t) {
  const flying = t >= FLOCK_T0 && t < FLOCK_T1;
  for (const b of FLOCK) {
    const s = flying ? clamp(((t - FLOCK_T0) / (FLOCK_T1 - FLOCK_T0) - b.del) / (1 - 2 * b.del), 0, 1) : 0;
    if (s <= 0 || s >= 1) { R(b.x, b.y, 2, 1, '#3a3448'); R(b.x + (hash(b.i, step(t, 240), 8) < 0.5 ? 0 : 1), b.y - 1, 1, 1, '#7a7288'); continue; }
    const e = s < 0.5 ? Math.sqrt(Math.sin(Math.PI * s)) : Math.pow(Math.sin(Math.PI * s), 1.5), th = b.phi + TAU * b.laps * s;
    const x = Math.round(b.x + (b.rx * (Math.cos(th) - Math.cos(b.phi)) + b.bx) * e), y = Math.round(b.y + (b.ry * (Math.sin(th) - Math.sin(b.phi)) - b.by) * e);
    const glide = s > 0.15 && s < 0.85 && hash(b.i, step(t, 480), 9) < 0.3;
    FLAP[glide ? 1 : (Math.floor(t * 9 + b.i * 0.7) % 3)].forEach((row, j) => { for (let i = 0; i < 5; i++) if (row[i] !== '.') R(x - 2 + i, y - 1 + j, 1, 1, row[i] === '2' ? '#4a4458' : '#d8cfd4'); });
  }
}
function drawFireflies(t) {
  FIREFLY_HOMES.forEach(([hx, hy], i) => {
    const b = Math.max(0, Math.sin(TAU * ((40 + (i * 7) % 31) * t / LOOP + i * 0.37))) ** 3;
    if (b < 0.06) return;
    const x = hx + 5 * Math.sin(TAU * ((3 + i % 4) * t / LOOP + i * 0.21)), y = hy + 3 * Math.sin(TAU * ((5 + i % 3) * t / LOOP + i * 0.53));
    if (y > RAIL - 2) return;
    ctx.globalAlpha = b; R(Math.round(x), Math.round(y), 1, 1, '#eaff9a'); ctx.globalAlpha = 1;
    halo(HL.fire, x, y, b * 0.8);
  });
}

/* ================= Things that pass: the scull and its coach, the renaioli punt, the little bus, cars on the bridge ================= */
const COURSE = { x0: 112, y0: 163, x1: 292, y1: 185 };  // from Ponte alle Grazie's second arch up to the pool below the weir
const TURN = 8;                                        // seconds to turn the boat round
/** where a boat on the scull's course is at loop time t (s: its outing; lag: seconds behind): x, y, heading, how far out (0..1) */
function courseAt(s, t, lag = 0) {
  const d = t - s.t0 - lag, leg = (s.t1 - s.t0 - TURN - 6) / 2;
  if (d < 0 || d > 2 * leg + TURN) return null;
  const u = d < leg ? d / leg : d < leg + TURN ? 1 : 1 - (d - leg - TURN) / leg, e = Math.sin(u * Math.PI / 2);
  const turn = d >= leg && d < leg + TURN ? (d - leg) / TURN : -1;
  return { x: lerp(COURSE.x0, COURSE.x1, e), y: lerp(COURSE.y0, COURSE.y1, e), dir: d < leg + TURN / 2 ? 1 : -1, e, turn, a: Math.min(1, d / 2.5, (2 * leg + TURN - d) / 2.5) };
}
function drawBoats(t, crowd) {
  for (const s of SCULL) {
    if (t < s.t0 || t >= s.t1) continue;
    const q = crowd > 0 ? courseAt(s, t) : null;
    if (q) {                                                                              // a double scull: white shell, two rowers, four blades
      const L = Math.round(lerp(11, 17, q.e) * (q.turn < 0 ? 1 : Math.max(0.35, Math.abs(Math.cos(Math.PI * q.turn))))), X = Math.round(q.x), Y = Math.round(q.y);
      const ph = frac(t / 2.4), drive = ph < 0.4 ? ph / 0.4 : 1 - (ph - 0.4) / 0.6, sx = Math.round(lerp(3, -3, drive)) * q.dir;
      ctx.globalAlpha = q.a * 0.4; R(X - (L >> 1) + 1, Y + 1, L - 2, 1, '#0c1030');
      for (let k = 1; k <= 5; k++) { ctx.globalAlpha = q.a * (0.5 - k * 0.08); R(X - q.dir * ((L >> 1) + k * 3), Y + (k & 1), 2, 1, '#dfe4ff'); }   // the wake
      ctx.globalAlpha = q.a;
      R(X - (L >> 1), Y, L, 1, '#ece6d8'); R(X - (L >> 1), Y, 1, 1, '#a8a4b0'); R(X + (L >> 1), Y, 1, 1, '#a8a4b0');
      for (const o of [-2, 2]) {
        const rx = X + o - (ph < 0.4 ? 0 : q.dir);
        R(rx, Y - 2, 1, 2, o < 0 ? '#c8423a' : '#2f5fa8'); R(rx, Y - 3, 1, 1, '#d8a888');
        R(rx + Math.round(sx / 2), Y + 1, 1, 1, '#8a8498'); R(rx + sx, Y + 2, 1, 1, ph < 0.4 ? '#fff6e0' : '#b8b4c4');   // the near blade (the far one hides behind the shell)
      }
      ctx.globalAlpha = 1;
    }
    const c = s.coach ? courseAt(s, t, 5) : null;
    if (c) {                                                                              // the coach's launch, a few lengths astern
      const X = Math.round(c.x - c.dir * 4), Y = Math.round(c.y + 4);
      ctx.globalAlpha = c.a;
      for (let k = 1; k <= 4; k++) { ctx.globalAlpha = c.a * (0.5 - k * 0.1); R(X - c.dir * (3 + k * 3), Y + 1 + (k & 1), 2, 1, '#dfe4ff'); }
      ctx.globalAlpha = c.a;
      R(X - 4, Y, 8, 2, '#3a3448'); R(X - 4, Y, 8, 1, '#8a8498'); R(X - c.dir, Y - 2, 1, 2, '#e8823a'); R(X - c.dir, Y - 3, 1, 1, '#d8a888');
      R(X + c.dir * 3, Y - 1, 1, 1, '#fff6e0'); R(X - c.dir * 4, Y - 1, 1, 1, c.dir > 0 ? '#ff5040' : '#60ff90');
      ctx.globalAlpha = 1;
      halo(HL.twink, X + c.dir * 3, Y - 1, c.a);
    }
  }
  if (crowd > 0 && t >= PUNT.t0 && t < PUNT.t1) {                                           // a renaioli punt drifting down with a lantern
    const u = (t - PUNT.t0) / (PUNT.t1 - PUNT.t0), X = Math.round(lerp(338, 118, u)), Y = Math.round(lerp(191, 160, u) + 0.6 * wave(t, 60));
    const a = Math.min(1, u * 12, (1 - u) * 14), push = frac(t / 4);
    ctx.globalAlpha = a * 0.4; R(X - 6, Y + 2, 13, 1, '#0c1030');
    ctx.globalAlpha = a;
    R(X - 7, Y, 15, 2, '#3a2a26'); R(X - 7, Y, 15, 1, '#8a6a48'); R(X - 8, Y - 1, 2, 1, '#8a6a48'); R(X + 7, Y - 1, 2, 1, '#8a6a48');
    for (const px of [-2, 1]) { R(X + px, Y - 2, 1, 2, px < 0 ? '#e8c040' : '#58b0d8'); R(X + px, Y - 3, 1, 1, '#d8a888'); }   // two passengers
    R(X + 5, Y - 4, 1, 4, '#ece6da'); R(X + 5, Y - 5, 1, 1, '#d8a888'); R(X + 5, Y - 6, 1, 1, '#c8b88a');                       // the boatman and his straw hat
    for (let k = 0; k < 8; k++) R(X + 6 + Math.round(k * (push < 0.5 ? 0.25 : 0.6)), Y - 5 + k, 1, 1, '#b89a70');               // his pole
    R(X - 6, Y - 3, 1, 3, '#2a2028'); R(X - 6, Y - 4, 1, 1, '#ffe9a0');
    ctx.globalAlpha = 1;
    halo(HL.punt, X - 6, Y - 4, a * (0.85 + 0.15 * wave(t, 300))); streak(ctx, t, X - 6, 3, '#ffc070', 0.4 * a, 77, Y + 2, Y + 14);
  }
}
/** the bus on the lungarno at loop time t: its left edge and direction, or null */
function busAt(t) {
  for (const b of BUS) if (t >= b.t0 && t < b.t1) { const u = (t - b.t0) / (b.t1 - b.t0); return { x: b.dir > 0 ? lerp(140, 486, u) : lerp(486, 140, u), dir: b.dir }; }
  return null;
}
function drawBus(t) {
  const b = busAt(t);
  if (!b) return;
  const X = Math.round(b.x), Y = bankY(clamp(X + 5, 0, W - 1)) - 8;
  if (X < 152) return;                                                                    // (it comes off the bridge behind the corner house)
  R(X, Y, 11, 5, '#e6dfcc'); R(X, Y, 11, 1, '#fff8e8'); R(X, Y + 4, 11, 1, '#3a5a8a');
  R(X + 1, Y + 1, 9, 2, '#ffe6a8'); for (const k of [3, 6]) R(X + k, Y + 1, 1, 2, '#6a5a50');
  R(b.dir > 0 ? X + 10 : X, Y + 3, 1, 1, '#fffbe0'); R(b.dir > 0 ? X : X + 10, Y + 3, 1, 1, '#ff4030');
  halo(HL.head, b.dir > 0 ? X + 11 : X - 1, Y + 3, 0.7); halo(HL.win, X + 5, Y + 2, 0.8);
}
function drawCars(t) {                                                                    // head and tail lamps crossing Ponte alle Grazie
  const j = Math.floor(t / 20), u = frac(t / 20) / 0.6;
  if (u >= 1) return;
  const d = j & 1 ? 1 : -1, x = d > 0 ? lerp(GRAZIE.x0, GRAZIE.x1, u) : lerp(GRAZIE.x1, GRAZIE.x0, u), y = Math.round(grazieY(x)) - 9;
  R(Math.round(x), y, 1, 1, '#fff4d0'); R(Math.round(x) - d * 2, y, 1, 1, '#ff5040'); halo(HL.twink, x, y, 0.8);
}

/* ================= Rose petals on the breeze, three depths (the city's own falling thing) ================= */
const PETAL_COLS = ['#f4a8bc', '#d16177', '#f8dcd8', '#c8364e', '#e8849c'];
const PETALS = [[], [], []];
{
  const r = rng(555);
  [[44, 6, 9, 3, 90], [26, 8, 12, 6, 130], [12, 10, 14, 10, 150]].forEach(([count, nMin, nMax, amp, drift], d) => {
    for (let i = 0; i < count; i++) PETALS[d].push({ id: d * 100 + i, n: nMin + Math.floor(r() * (nMax - nMin + 1)), ph: r(), ks: 30 + Math.floor(r() * 30), p2: r(),
      kf: 40 + Math.floor(r() * 50), p4: r(), amp: amp * (0.6 + r() * 0.8), drift: drift * (0.5 + r()), col: Math.floor(r() * 5) });
  });
}
const PETAL_LAND = [[150, 198], [206, 262], [H + 6, H + 6]];
function drawPetals(d, t) {
  const band = PETAL_LAND[d];
  for (const q of PETALS[d]) {
    const { cyc, u } = cycle(t, q.n, q.ph), sw = wave(t, q.ks, q.p2);
    const y0 = d === 2 ? -6 : (d ? 96 : 72) + hash(q.id, cyc, 7) * 56;                      // (they come up off the garden below, on the breeze)
    const yEnd = lerp(band[0], band[1], hash(q.id, cyc, 6)), y = y0 + u * (yEnd - y0) - Math.abs(sw) * (d + 1);
    const x = -q.drift + hash(q.id, cyc, 5) * (W + q.drift) + u * q.drift + sw * q.amp;
    if (x < -4 || x > W + 4) continue;
    const fl = Math.cos(TAU * (q.kf * t / LOOP + q.p4)), col = PETAL_COLS[(q.col + cyc) % 5], X = Math.round(x), Y = Math.round(y);
    ctx.globalAlpha = (band[0] < H && u > 0.88 ? (1 - u) / 0.12 : 1) * Math.min(1, u * 10) * (d === 0 ? 0.85 : 1);
    if (d === 0) R(X, Y, 1, 1, col);
    else if (d === 1) { if (Math.abs(fl) > 0.5) R(X, Y, 2, 1, col); else R(X, Y, 1, 2, col); }
    else if (fl > 0.4) { R(X, Y, 3, 2, col); R(X, Y, 2, 1, '#fff0ee'); }
    else if (fl < -0.4) { R(X, Y, 2, 3, col); R(X + 1, Y + 2, 1, 1, '#8a2c40'); }
    else R(X, Y + 1, 3, 1, col);
  }
  ctx.globalAlpha = 1;
}
// a picked weather (site/js/engine/weather.js): rose petals are the city's own; rain, snow and leaves fall behind the
// cathedral, onto the river and onto the steps, and catch the lamps, the cart and the hoarding's light
const WEATHER = weatherPlans(['rosepetals']);
const WX_LAND = [[150, 196], [170, RAIL - 2], [FOOT + 4, H - 2]];
const WX_LIGHTS = [...LAMPS.map(x => ({ x, y: LAMP_Y + 6, rx: 54, ry: 54 })), { x: CART.x + 25, y: CART.y + 4, rx: 30, ry: 22 }];
const WX = { land: WX_LAND, lights: WX_LIGHTS };
const WX_BB = { land: WX_LAND, lights: [...WX_LIGHTS, { x: BILL.x + 24, y: BILL.y + 4, rx: 30, ry: 16 }] };

/* ================= Per-frame: the hoarding ================= */
let ADS, PITCH;
function drawBillboard(t, pitch) {
  const lv = flick(t, BILL_FLICK, 13);
  halo(HL.billGlow, BILL.x + 24, BILL.y + 4, (0.9 + 0.1 * wave(t, 8, 0.2)) * (2 + lv) / 3);
  blit(SCAF_C);
  if (!PITCH.covers(t, pitch)) {
    ctx.drawImage(ADS[0], BILL.x, BILL.y);
    if (lv < 1) { ctx.globalAlpha = 1 - lv; ctx.drawImage(ADS[1], BILL.x, BILL.y); ctx.globalAlpha = 1; }
    const u = frac(t / 6), d = frac(t / 12);                                               // a glint on the scoop, a drip down the cone
    if (u < 0.14) { ctx.globalAlpha = Math.sin(Math.PI * u / 0.14); R(BILL.x + 3, BILL.y + 5, 3, 1, '#ffffff'); R(BILL.x + 4, BILL.y + 4, 1, 3, '#ffffff'); ctx.globalAlpha = 1; }
    if (d < 0.6) R(BILL.x + 8, BILL.y + 10 + Math.floor(d / 0.6 * 5), 1, 1, '#ee9058');
  }
  PITCH.draw(ctx, t, BILL.x, BILL.y, pitch);
  BILL_LAMPS.forEach((lx, i) => {
    const a = i === 2 ? lv : 1;
    if (a < 1) R(lx - 1, BILL_LAMP_Y, 3, 1, `rgba(40,26,18,${(0.85 * (1 - a)).toFixed(3)})`);
    halo(HL.billLamp, lx, BILL_LAMP_Y + 1, a * (0.92 + 0.08 * wave(t, 700 + i * 37)));
  });
}

/* ================= Prerendered state ================= */
let HL, SKY_C, MOON_C, CLOUDS, HILLS, HAZES, CITY_BACK, CITY_FRONT, PALAZZO_C, SCAF_C, WATER_C, MIRROR_C, WBUF, WCTX,
  GRAZIE_C, SOUTH_C, TOWER_C, DOME_WARM, VIG_C, FORE_C, CART_C, SCOOTER_C, TREES_C;
function renderVignette() {
  const p = pix(W, H), c = [8, 8, 26];
  for (let y = 0; y < H; y++) for (let i = 0; i < W; i++) {
    const v = smooth(0.74, 1.45, Math.hypot((i - W / 2) / (W / 2), (y - H / 2) / (H / 2) * 0.9));
    const q = Math.floor(v * 4 + bayer(i, y)) / 4;
    if (q > 0) p.set(i, y, c, Math.round(q * 0.42 * 255));
  }
  return p.done();
}
/** the dome's tiles a shade warmer: laid over the dome as its floodlights breathe (the slow motion on the landmark) */
function renderDomeWarm() {
  const p = pix(60, 40), cx = 30;
  for (let y = DUOMO.top; y <= DUOMO.spring; y++) {
    const hw = domeHW(y);
    for (let i = Math.ceil(-hw); i <= Math.floor(hw); i++) {
      const g = (y - DUOMO.top) / 36;
      if (g * 1.2 - 0.1 > bayer(cx + i, y) * 0.9) p.set(cx + i, y - DUOMO.top, '#ffb060', 150);
    }
  }
  return p.done();
}
function prerender() {
  HL = {
    moon: makeHalo(30, '#8e9ad8', 0.16, 1.8), moonCore: makeHalo(11, '#fff0d0', 0.3, 1.5), star: makeHalo(3, '#dfe6ff', 0.5, 1),
    duomo: makeHalo(74, '#ff9c58', 0.17, 1.6), duomoLow: makeHalo(46, '#ffc078', 0.2, 1.6), lantern: makeHalo(9, '#ffe6b0', 0.4, 1.3),
    palla: makeHalo(5, '#ffe2a0', 0.4, 1.4),
    camp: makeHalo(40, '#ffcf90', 0.16, 1.6), bell: makeHalo(30, '#ffd890', 0.3, 1.5),
    pv: makeHalo(44, '#ffb050', 0.18, 1.6), tower: makeHalo(34, '#ffb458', 0.2, 1.6), croce: makeHalo(40, '#ffb868', 0.13, 1.6),
    quay: makeHalo(6, '#ffd890', 0.4, 1.2), twink: makeHalo(3, '#ffd080', 0.45, 1), ponte: makeHalo(40, '#ffb060', 0.16, 1.6),
    win: makeHalo(7, '#ffb45a', 0.14), fire: makeHalo(4, '#d8f070', 0.5, 1.2), sun: makeHalo(90, '#ff9c6a', 0.14, 1.4),
    lamp: makeHalo(58, '#ffbe70', 0.24, 1.7), lampCore: makeHalo(10, '#fff0c8', 0.36, 1.3), bulb: makeHalo(6, '#ffd890', 0.45, 1.2),
    cart: makeHalo(30, '#ffc070', 0.2, 1.6), billGlow: makeHalo(36, '#ffd8a0', 0.14, 1.6), billLamp: makeHalo(6, '#ffe8b8', 0.5, 1.3),
    head: makeHalo(7, '#fff4d0', 0.5, 1.2), tail: makeHalo(3, '#ff4030', 0.5, 1), punt: makeHalo(12, '#ffc070', 0.34, 1.3),
    phone: makeHalo(5, '#bfe8ff', 0.4, 1.2), south: makeHalo(9, '#ffb060', 0.3, 1.3), strobe: makeHalo(4, '#ffffff', 0.6, 1),
  };
  SKY_C = renderSky(); MOON_C = renderMoon(); CLOUDS = CLOUD_DEFS.map(makeCloudLayer);
  HILLS = renderHills(); HAZES = HAZE_DEFS.map(makeHaze);
  const city = renderCity();
  MIRROR_C = renderMirror(city.front);
  CITY_BACK = trim(city.back); CITY_FRONT = trim(city.front);
  PALAZZO_C = renderPalazzo(); SCAF_C = renderScaffold();
  WATER_C = renderWater(); [WBUF, WCTX] = makeCanvas(W, WDEPTH);
  GRAZIE_C = renderGrazie(); SOUTH_C = renderSouth(); TOWER_C = renderTower();
  DOME_WARM = renderDomeWarm(); VIG_C = renderVignette();
  FORE_C = renderFore(); CART_C = renderCart(); SCOOTER_C = sprite(SCOOTER, SCOOTER_KEY); TREES_C = renderFrameTrees();
  ADS = [renderAd(false), renderAd(true)]; PITCH = makePitch(BILL.w, BILL.h, { accent: '#d16177' });
  PIG = {}; for (const k in PIGEON) PIG[k] = [sprite(PIGEON[k], PIG_COL), sprite(PIGEON[k], PIG_COL, true)];
  FIASCO = [sprite(FIASCO_ROWS[0], FIASCO_KEY), sprite(FIASCO_ROWS[1], FIASCO_KEY), sprite(FIASCO_ROWS[1], FIASCO_KEY, true)];
  CAT_C = { sit: sprite(CAT_SIT, CAT_KEY), walk: [0, 1].map(fl => CAT_WALK_F.map(rows => sprite(rows, CAT_KEY, !!fl))) };
}

/* ================= Per-frame: the sky ================= */
function drawScroll(cv, y, k, t, dir = 1) {
  let off = scroll(t, k, W);
  if (dir < 0) off = (W - off) % W;
  ctx.drawImage(cv, off - W, y); ctx.drawImage(cv, off, y);
}
function drawJet(t) {                                  // high and far: the body dark, the contrail still in the sun
  if (t < JET.t0 || t >= JET.t1) return;
  const u = (t - JET.t0) / (JET.t1 - JET.t0), x = lerp(500, -60, u), y = lerp(24, 6, u);   // (it clears the moon)
  for (let k = 2; k < 46; k++) {
    const a = (1 - k / 46) * 0.7 * Math.min(1, u * 12);
    ctx.globalAlpha = a; R(Math.round(x + k), Math.round(y + k * 0.032), 1, 1, k < 14 ? '#ffc8a8' : '#e89aa0');
  }
  ctx.globalAlpha = 1;
  R(Math.round(x), Math.round(y), 2, 1, '#dfe6ff');
  if (frac(t / 1.5) < 0.12) halo(HL.strobe, x, y, 0.7);
}
function drawSky(t) {
  ctx.drawImage(SKY_C, 0, 0);
  halo(HL.sun, GLOW.x, GLOW.y - 6, 0.8 + 0.2 * wave(t, 2));
  for (const s of STARS) {
    const a = s.b * (0.5 + 0.5 * wave(t, s.k, s.ph));
    ctx.globalAlpha = a; R(s.x, s.y, 1, 1, '#e6ecff');
    if (s.big) { ctx.globalAlpha = a * 0.5; R(s.x - 1, s.y, 3, 1, '#b8c4f0'); R(s.x, s.y - 1, 1, 3, '#b8c4f0'); }
  }
  ctx.globalAlpha = 1;
  halo(HL.moon, MOON.x, MOON.y, 0.9); halo(HL.moonCore, MOON.x - 2, MOON.y + 2, 0.8);
  ctx.drawImage(MOON_C, MOON.x - 7, MOON.y - 7);
  R(MOON.x + 22, MOON.y + 13, 1, 1, '#fff6de'); halo(HL.star, MOON.x + 22, MOON.y + 13, 0.7 + 0.3 * wave(t, 37));   // the evening star
  drawScroll(CLOUDS[0].c, CLOUDS[0].y, CLOUDS[0].k, t);
  drawJet(t);
  drawScroll(CLOUDS[1].c, CLOUDS[1].y, CLOUDS[1].k, t);
  drawScroll(CLOUDS[2].c, CLOUDS[2].y, CLOUDS[2].k, t);
}
function drawHaze(f, t, breathe) {
  ctx.globalAlpha = f.alpha * (0.8 + 0.2 * wave(t, breathe, f.seed * 0.1));
  drawScroll(f.c, f.y, f.k, t, f.dir);
  ctx.globalAlpha = 1;
}
function drawHills(t) {
  blit(HILLS);
  for (const l of HILL_LIGHTS) { ctx.globalAlpha = 0.5 + 0.5 * wave(t, l.k, l.ph); R(l.x, l.y, 1, 1, '#ffd9a0'); }
  ctx.globalAlpha = 1;
  drawHaze(HAZES[0], t, 3);
}

/* ================= Per-frame: the city ================= */
function drawWindows(t, layer, bare) {
  for (const w of WINS) {
    if (w.layer !== layer || (w.under && !bare)) continue;
    if (hash(w.id, Math.floor(frac(t / LOOP + w.off) * w.n), 5) < w.p) R(w.x, w.y, w.w, w.h, w.col);
  }
}
const bellLevel = t => { let b = 0; for (const tb of BELLS) { const dt = t - tb; if (dt >= 0 && dt < 3) b = Math.max(b, Math.exp(-dt * 1.2) * (1 - dt / 3)); } return b; };
function drawCityBack(t) {
  const cx = DUOMO.cx, warm = 0.5 + 0.5 * wave(t, 2, 0.75);                    // the floodlights' slow breath: two a loop
  halo(HL.duomo, cx, 86, 0.8 + 0.2 * warm); halo(HL.camp, 186, 92, 0.9); halo(HL.pv, 118, 112, 0.9); halo(HL.croce, 372, 116, 0.9);
  blit(CITY_BACK);
  drawWindows(t, 0, false);
  ctx.globalAlpha = 0.16 + 0.34 * warm; ctx.drawImage(DOME_WARM, cx - 30, DUOMO.top); ctx.globalAlpha = 1;
  halo(HL.duomoLow, cx, 106, 0.7 + 0.3 * warm); halo(HL.lantern, cx, 40, 0.6 + 0.4 * warm);
  const gl = frac(t / 8 + 0.4), glint = gl < 0.1 ? Math.sin(Math.PI * gl / 0.1) : 0;   // the palla catches the light now and then
  halo(HL.palla, cx, PALLA_Y + 1, 0.4 + 0.25 * warm + 0.5 * glint);
  if (glint > 0) { ctx.globalAlpha = glint; dot(cx - 1, PALLA_Y, '#ffffff'); ctx.globalAlpha = 1; }
  const b = bellLevel(t);                                                     // a stroke: the belfry's openings glow, the sound spreads
  if (b > 0.02) {
    ctx.globalAlpha = b;
    for (const [bx, by, bw, bh] of BELFRY) R(bx, by + 3, bw, bh - 3, '#ffb858');
    ctx.globalAlpha = 1;
    halo(HL.bell, CAMP.x0 + 6, 68, b);
  }
  for (const [tc, bx, by] of [[CHIME_T, 161, 96], [CROCE_CHIME_T, 402, 86]]) {                      // the smaller bells: a soft glow in the belfry
    const dt = t - tc;
    if (dt >= 0 && dt < 4) halo(HL.bell, bx, by, 0.6 * Math.sin(Math.PI * dt / 4) * (0.7 + 0.3 * Math.cos(dt * 4.5)));
  }
  for (const tb of BELLS) {
    const dt = t - tb;
    if (dt < 0 || dt > 2.6) continue;
    const rr = 6 + dt * 30, n = Math.floor(rr * 2);
    ctx.globalCompositeOperation = 'lighter'; ctx.fillStyle = '#ffd890'; ctx.globalAlpha = (1 - dt / 2.6) * 0.4;
    for (let j = 0; j < n; j++) { const a = j / n * TAU; ctx.fillRect(Math.round(CAMP.x0 + 6 + Math.cos(a) * rr), Math.round(68 + Math.sin(a) * rr * 0.8), 1, 1); }
    ctx.globalAlpha = 1; ctx.globalCompositeOperation = 'source-over';
  }
}
function drawCityFront(t) {
  blit(CITY_FRONT);
  drawWindows(t, 1, false);
  halo(HL.ponte, 46, 146, 0.8 + 0.1 * wave(t, 9));
  for (const q of TWINK) {
    const a = 0.55 + 0.45 * wave(t, q.k, q.ph);
    ctx.globalAlpha = a; R(q.x, q.y, 1, 1, '#ffe2a0'); ctx.globalAlpha = 1;
    halo(HL.twink, q.x, q.y, a);
  }
  QUAY_LAMPS.forEach(([x, y], i) => halo(HL.quay, x, y, 0.8 + 0.2 * wave(t, 300 + i * 17, i * 0.3)));
  drawSmoke(t);
}

/* a few kitchens' chimneys smoke into the evening: thin plumes leaning with the breeze */
const SMOKES = [[62, 122], [164, 128], [300, 134], [392, 140], [436, 132]];
function drawSmoke(t) {
  ctx.fillStyle = '#9a94b4';
  SMOKES.forEach(([sx, sy], i) => {
    for (let k = 0; k < 7; k++) {
      const { u } = cycle(t, 20 + i * 3, k / 7 + i * 0.13), x = sx + u * u * 14 + wave(t, 13 + k * 3, k * 0.17 + i * 0.3) * 1.6 * u, y = sy - u * 15;
      ctx.globalAlpha = 0.5 * Math.min(1, u * 7) * (1 - u) ** 1.3;
      ctx.fillRect(Math.round(x), Math.round(y), u > 0.45 ? 2 : 1, u > 0.7 ? 2 : 1);
    }
  });
  ctx.globalAlpha = 1;
}

/* ================= Per-frame: the Arno ================= */
const RIPPLES = [];
{ const r = rng(91); for (let k = 2; k < WDEPTH; k += 2) for (let j = 0; j < 4; j++) RIPPLES.push({ k: k + (j & 1), sp: 2 + Math.floor(r() * 6), ph: r(), len: 4 + Math.floor(r() * 12), glint: j === 3 }); }
const FOAM = [];
{ const r = rng(92); for (let i = 0; i < 46; i++) FOAM.push({ id: i, n: 30 + Math.floor(r() * 30), ph: r() }); }
function drawRiver(t) {
  const c = WCTX;
  c.globalCompositeOperation = 'copy'; c.globalAlpha = 1; c.drawImage(WATER_C, 0, 0); c.globalCompositeOperation = 'source-over';
  for (let k = 0; k < 24; k++) {                                              // the far bank, mirrored and rippling
    const off = Math.round(Math.sin(TAU * 45 * t / LOOP + k * 0.55) * (0.6 + k * 0.06) + Math.sin(TAU * 110 * t / LOOP - k * 1.3) * 0.7);
    c.globalAlpha = 0.46 * (1 - k / 24);
    c.drawImage(MIRROR_C, 0, k, W, 1, off, k, W, 1);
  }
  for (const q of RIPPLES) {                                                  // troughs and glints drifting downstream (left)
    const x = Math.round(frac(q.ph - q.sp * t / LOOP) * (W + 40)) - 20;
    if (x > WEIR.x1 - q.k * 3.7) continue;                                    // (the pool above the weir lies still)
    c.globalAlpha = q.glint ? 0.28 : 0.14; c.fillStyle = q.glint ? (x < 200 ? '#f8c8a0' : '#9aa4d8') : '#141a3c';
    c.fillRect(x, q.k, q.len, 1);
  }
  QUAY_LAMPS.forEach(([x], i) => streak(c, t, x, 2, '#ffd890', 0.42, i + 1, 1, 26));
  for (let i = 0; i < 6; i++) streak(c, t, 20 + i * 11, 3, '#ffc070', 0.3, 40 + i, 0, 9);          // Ponte Vecchio's shop lights
  const bus = busAt(t);
  if (bus && bus.x > 152) streak(c, t, Math.round(bus.x) + 5, 5, '#ffe6a8', 0.3, 55, 1, 18);
  for (let j = 0; j < 7; j++) {                                               // long slow sheens: the sky lying on calm water
    const k = 5 + j * 6, len = 40 + (j * 23) % 50, x = Math.round(frac(j * 0.37 - (1 + (j & 1)) * t / LOOP) * (W + 120)) - 60;
    c.globalAlpha = 0.1 + 0.05 * wave(t, 7 + j, j * 0.3); c.fillStyle = x < 220 ? '#f0b8a0' : '#8e9ad4';
    c.fillRect(x, k, len, 1); c.fillRect(x + 6, k + 1, len - 14, 1);
  }
  // the weir: a white crest slanting across the river, an apron of foam on its downstream (left) side, flecks sliding off
  const fs = step(t, 480);
  for (let k = 0; k <= 27; k++) {
    const xc = Math.round(WEIR.x1 - k * (WEIR.x1 - WEIR.x0) / 27);
    c.globalAlpha = 0.3; c.fillStyle = '#10163a'; c.fillRect(xc + 1, k, 5, 1);                      // the lip, dark under smooth water
    c.globalAlpha = 0.95; c.fillStyle = '#f2f4ff'; c.fillRect(xc - 3, k, 4, 1);
    for (let d = 4; d <= 12; d++) {
      const h = hash(xc - d, k, fs);
      if (h > 0.8 - d * 0.06) continue;
      c.globalAlpha = 0.75 - d * 0.05; c.fillStyle = h < 0.2 ? '#ffffff' : '#c4ccf0'; c.fillRect(xc - d, k, 1, 1);
    }
  }
  for (const f of FOAM) {
    const { cyc, u } = cycle(t, f.n, f.ph), x0 = WEIR.x0 + hash(f.id, cyc, 72) * (WEIR.x1 - WEIR.x0), k = weirK(x0) + u * 2;
    c.globalAlpha = (1 - u) * 0.6; c.fillStyle = '#dfe4ff'; c.fillRect(Math.round(x0 - 12 - u * 22), Math.round(k), 1 + (f.id & 1), 1);
  }
  c.globalAlpha = 1;
  for (const s of STRIPS) ctx.drawImage(WBUF, s.x, 0, s.w, WDEPTH, s.x, s.y, s.w, WDEPTH);
}
function drawSouth(t) {
  blit(GRAZIE_C);
  drawCars(t);
  GRAZIE_LAMPS.forEach(([x, y], i) => { halo(HL.quay, x, y, 0.8 + 0.2 * wave(t, 280 + i * 23, i * 0.2)); streak(ctx, t, x, 2, '#ffd890', 0.34, 60 + i, y + 12, y + 26); });
  blit(SOUTH_C);
  drawWindows(t, 3, false);
  for (const [x, y] of SOUTH_LAMPS) halo(HL.south, x, y, 0.85);
  halo(HL.tower, (TOWER.x0 + TOWER.x1) >> 1, RAIL - 6, 0.9 + 0.1 * wave(t, 7));
  blit(TOWER_C);
}

/* ================= Frame compositor (back to front) ================= */
// pitch: the site's face on the hoarding (promo captures, the website); billboard: false leaves the scaffold off the
// palazzo (the loop videos); weather: a WEATHERS pick (site/js/engine/weather.js), the city's own when left out;
// crowd: the share of the people to show, 0..1 (CROWD when left out or not a number); 0 = nobody at all
function renderFrame(t, { pitch, billboard = true, weather, reducedMotion = false, crowd: share } = {}) {
  t = ((t % LOOP) + LOOP) % LOOP;
  ctx.setTransform(1, 0, 0, 1, 0, 0); ctx.globalAlpha = 1; ctx.globalCompositeOperation = 'source-over';
  const wx = WEATHER(weather), wxc = billboard ? WX_BB : WX;
  const crowd = clamp(typeof share === 'number' && share === share ? share : CROWD, 0, 1);
  R(0, SKY_H, W, H - SKY_H, '#141a34');
  drawSky(t);
  drawHills(t);
  drawSwifts(t, false);
  drawWeather(ctx, wx.shared, 0, t, wxc, reducedMotion);
  drawCityBack(t);
  drawFlock(t);
  blit(PALAZZO_C); drawWindows(t, 2, !billboard);
  if (billboard) drawBillboard(t, pitch);                    // behind the lungarno's roofs, below the cathedral
  drawHaze(HAZES[1], t, 4);
  drawCityFront(t);
  drawBus(t);
  drawRiver(t);
  drawBoats(t, crowd);
  drawSouth(t);
  drawSwifts(t, true, billboard);
  drawFireflies(t);
  if (wx.rosepetals) drawPetals(0, t);
  drawWeather(ctx, wx.shared, 1, t, wxc, reducedMotion);
  blit(FORE_C);
  drawHedges(t);
  drawStepPigeons(t);
  drawCat(t);
  drawCartLife(t, crowd);
  ctx.drawImage(SCOOTER_C, 398, TY - 10);
  drawCast(t, crowd);
  drawLampLife(t);
  if (wx.rosepetals) drawPetals(1, t);
  drawTrees(t);
  if (wx.rosepetals) drawPetals(2, t);
  drawWeather(ctx, wx.shared, 2, t, wxc, reducedMotion);
  ctx.drawImage(VIG_C, 0, 0);
}

export default {
  id: 'florence',
  name: 'Florence',
  tagline: 'Blue hour over the Arno',
  timeZone: 'Europe/Rome',
  accent: '#d16177',                                   // terracotta rose, written out: tools/seo/seo.mjs reads it from the source
  focusX: 241,
  landmark: [180, 302],                                // Giotto's campanile and Brunelleschi's dome with its tribunes
  landmarkTop: PALLA_Y - 4,                            // the row of the lantern's cross
  billboard: BILL,
  perch: { dx: 11 },                                   // the What's new pigeon (site/js/ui/pigeon.js): between the lamps
  crowd: CROWD,                                        // the share of the people shown by default; render(t, { crowd }) thins them, 0 = nobody
  ambience: { birds: 0.35, crickets: 0.3, water: 0.3, crowd: 0.15 },
  events: [
    { t: CROCE_CHIME_T, type: 'chime', x: 402, gain: 0.3 },                                           // Santa Croce
    ...BELLS.map(t => ({ t, type: 'bell', x: 186 })),                                      // the campanile
    { t: SCULL[1].t0 + 5, type: 'boat', dur: 40, pan: [-0.5, 0.2], gain: 0.2 },            // the coach's launch, far below
    { t: CHIME_T, type: 'chime', x: 161, gain: 0.4 },                                      // the Badia
  ],
  create(c) {
    ctx = c;
    ctx.imageSmoothingEnabled = false;
    prerender();
    return { render: renderFrame };
  },
};
